// @ts-nocheck
/**
 * AI分析画面
 *
 * AIシミュレーターのデバッグ機能をUI上で使用可能にする画面。
 * AIがどのような判断で行動を選択しているかを可視化し、デバッグ・調整に活用する。
 */

import React, { useState, useMemo } from 'react';
import { simulateGameDebug, simulateMultiple } from '../../engine/gameEngine/Simulator';
import { getDeckByName } from '../../engine/gameEngine/debugHelper';
import { PREBUILT_DECKS } from '../../decks/prebuiltDecks';
import { DECKS as NPC_DECKS, COMPETITORS, expandDeckToCardIds } from '../tournament/data/competitors';
import { useIsMobile } from '../hooks/useMediaQuery';
import { shuffle } from '../../utils/helpers';
import { getPlayerDeckSettings } from '../../engine/ai/aiController';

// ========================================
// 定数
// ========================================

const DIFFICULTIES = [
  { value: 'easy', label: 'かんたん' },
  { value: 'normal', label: 'ふつう' },
  { value: 'hard', label: 'むずかしい' },
  { value: 'evolved', label: '🧬 進化型AI' },
  { value: 'evolvedSim', label: '🔬 進化型AI(実験)' },
];

const TABS = [
  { id: 'all', label: '全体' },
  { id: 'status', label: '状況' },
  { id: 'magic', label: '魔法' },
  { id: 'setsuna', label: '刹那' },
  { id: 'summon', label: '召喚' },
  { id: 'skill', label: 'スキル' },
  { id: 'attack', label: '攻撃' },
  { id: 'charge', label: 'チャージ' },
  { id: 'fieldCard', label: 'フィールド' },
  { id: 'phaseCard', label: 'フェイズ' },
];

const DECK_CATEGORIES = [
  { id: 'preset', label: 'プリセット' },
  { id: 'npc', label: 'NPC' },
  { id: 'mydeck', label: 'マイデッキ' },
];

// ========================================
// スタイル
// ========================================

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3a 100%)',
    zIndex: 1000,
    overflow: 'auto',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '16px 20px',
    background: 'rgba(30, 30, 60, 0.8)',
    borderRadius: '12px',
    border: '1px solid rgba(100, 100, 200, 0.3)',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ffd700',
    margin: 0,
  },
  closeButton: {
    padding: '8px 16px',
    fontSize: '14px',
    background: 'rgba(100, 100, 150, 0.5)',
    border: '1px solid rgba(150, 150, 200, 0.5)',
    borderRadius: '8px',
    color: '#e0e0e0',
    cursor: 'pointer',
  },
  panel: {
    background: 'rgba(30, 30, 60, 0.6)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid rgba(100, 100, 200, 0.2)',
  },
  panelTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#a0a0ff',
    marginBottom: '12px',
    borderBottom: '1px solid rgba(100, 100, 200, 0.3)',
    paddingBottom: '8px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  label: {
    fontSize: '14px',
    color: '#c0c0c0',
    minWidth: '80px',
  },
  select: {
    padding: '8px 12px',
    fontSize: '14px',
    background: 'rgba(20, 20, 40, 0.8)',
    border: '1px solid rgba(100, 100, 200, 0.4)',
    borderRadius: '6px',
    color: '#e0e0e0',
    minWidth: '140px',
  },
  input: {
    padding: '8px 12px',
    fontSize: '14px',
    background: 'rgba(20, 20, 40, 0.8)',
    border: '1px solid rgba(100, 100, 200, 0.4)',
    borderRadius: '6px',
    color: '#e0e0e0',
    width: '80px',
  },
  runButton: {
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #4a90d9 0%, #357abd 100%)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    cursor: 'pointer',
    marginTop: '8px',
  },
  runButtonDisabled: {
    background: 'rgba(100, 100, 150, 0.5)',
    cursor: 'not-allowed',
  },
  smallButton: {
    padding: '6px 12px',
    fontSize: '12px',
    border: 'none',
    borderRadius: '4px',
    color: 'white',
    cursor: 'pointer',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px',
  },
  summaryItem: {
    background: 'rgba(20, 20, 40, 0.6)',
    padding: '12px',
    borderRadius: '8px',
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ffd700',
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#a0a0a0',
    marginTop: '4px',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '8px 16px',
    fontSize: '14px',
    background: 'rgba(40, 40, 80, 0.6)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(100, 100, 200, 0.3)',
    borderRadius: '6px',
    color: '#c0c0c0',
    cursor: 'pointer',
  },
  tabActive: {
    background: 'rgba(70, 70, 150, 0.8)',
    borderColor: '#6b6bff',
    color: '#ffffff',
  },
  filters: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  logContainer: {
    background: 'rgba(10, 10, 25, 0.8)',
    borderRadius: '8px',
    padding: '12px',
    maxHeight: '400px',
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.6',
  },
  logTurn: {
    color: '#ffd700',
    fontWeight: 'bold',
    fontSize: '14px',
    marginTop: '16px',
    marginBottom: '8px',
  },
  logPlayer: {
    color: '#a0a0ff',
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  logEntry: {
    marginBottom: '8px',
    padding: '8px',
    background: 'rgba(30, 30, 50, 0.5)',
    borderRadius: '4px',
  },
  logPhase: {
    color: '#888',
    fontSize: '12px',
  },
  logAction: (executed) => ({
    color: executed ? '#6bff6b' : '#ff6b6b',
    fontWeight: 'bold',
  }),
  logDetail: {
    color: '#c0c0c0',
    fontSize: '12px',
    marginTop: '4px',
  },
  error: {
    background: 'rgba(150, 50, 50, 0.4)',
    border: '1px solid rgba(200, 100, 100, 0.5)',
    borderRadius: '8px',
    padding: '16px',
    color: '#ff8080',
  },
  deckCategoryTabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '8px',
  },
  deckCategoryTab: {
    padding: '4px 10px',
    fontSize: '11px',
    background: 'rgba(40, 40, 80, 0.5)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(100, 100, 200, 0.2)',
    borderRadius: '4px',
    color: '#a0a0a0',
    cursor: 'pointer',
  },
  deckCategoryTabActive: {
    background: 'rgba(70, 70, 150, 0.7)',
    borderColor: '#6b6bff',
    color: '#ffffff',
  },
  deckOwner: {
    fontSize: '10px',
    color: '#888',
    marginLeft: '4px',
  },
  modeToggle: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  modeButton: {
    padding: '8px 16px',
    fontSize: '14px',
    background: 'rgba(40, 40, 80, 0.6)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(100, 100, 200, 0.3)',
    borderRadius: '6px',
    color: '#c0c0c0',
    cursor: 'pointer',
  },
  modeButtonActive: {
    background: 'linear-gradient(135deg, #4a90d9 0%, #357abd 100%)',
    borderColor: '#6b9dff',
    color: '#ffffff',
  },
  batchSummary: {
    background: 'rgba(20, 40, 60, 0.6)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid rgba(100, 150, 200, 0.3)',
  },
  winRateBar: {
    height: '24px',
    borderRadius: '12px',
    overflow: 'hidden',
    display: 'flex',
    marginBottom: '8px',
  },
  winRateSegment: (color, width) => ({
    background: color,
    width: `${width}%`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: 'white',
    textShadow: '0 0 4px rgba(0,0,0,0.5)',
  }),
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '8px',
    marginTop: '12px',
  },
  statItem: {
    background: 'rgba(30, 30, 50, 0.5)',
    padding: '10px',
    borderRadius: '8px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#ffd700',
  },
  statLabel: {
    fontSize: '11px',
    color: '#a0a0a0',
    marginTop: '2px',
  },
  gameList: {
    maxHeight: '200px',
    overflow: 'auto',
    marginTop: '12px',
    background: 'rgba(10, 10, 25, 0.5)',
    borderRadius: '8px',
    padding: '8px',
  },
  gameItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    borderBottom: '1px solid rgba(100, 100, 200, 0.1)',
    fontSize: '12px',
  },
};

// ========================================
// ヘルパー関数
// ========================================

const getCategoryLabel = (category) => {
  const labels = {
    magic: '魔法',
    setsuna: '刹那魔法',
    summon: '召喚',
    skill: 'スキル',
    attack: '攻撃',
    charge: 'チャージ',
    phaseCardCharge: 'フェイズチャージ',
    status: '状況',
    phase: 'フェイズ',
    fieldCard: 'フィールド',
    phaseCard: 'フェイズカード',
  };
  return labels[category] || category;
};

const getActionLabel = (action) => {
  const labels = {
    used: '使用',
    skipped: '見送り',
    executed: '実行',
    activated: '発動',
    placed: '設置',
    none: 'なし',
  };
  return labels[action] || action;
};

const getTypeIcon = (type) => {
  const icons = {
    monster: '⚔️',
    magic: '✨',
    field: '🏔️',
    phase: '🌀',
  };
  return icons[type] || '📄';
};

// ========================================
// サブコンポーネント
// ========================================

// デッキ選択コンポーネント
const DeckSelector = ({
  playerLabel,
  deckCategory, setDeckCategory,
  deck, setDeck,
  difficulty, setDifficulty,
  userDecks,
}) => {
  // カテゴリごとのデッキオプションを生成
  const getDeckOptions = () => {
    switch (deckCategory) {
      case 'preset':
        return Object.entries(PREBUILT_DECKS).map(([key, d]) => ({
          value: `preset:${key}`,
          label: d.name,
        }));
      case 'npc':
        return Object.entries(NPC_DECKS).map(([name, d]) => {
          const owner = COMPETITORS[d.owner];
          return {
            value: `npc:${name}`,
            label: `${d.name}`,
            owner: owner?.name || d.owner,
          };
        });
      case 'mydeck':
        if (!userDecks || userDecks.length === 0) {
          return [{ value: '', label: '(デッキなし)', disabled: true }];
        }
        return userDecks.map((d, idx) => ({
          value: `mydeck:${idx}`,
          label: d.name || `デッキ${idx + 1}`,
          cardCount: d.cards?.length || 0,
        }));
      default:
        return [];
    }
  };

  const deckOptions = getDeckOptions();

  // カテゴリ変更時に最初のデッキを選択
  const handleCategoryChange = (newCategory) => {
    setDeckCategory(newCategory);
    const options = (() => {
      switch (newCategory) {
        case 'preset':
          return Object.entries(PREBUILT_DECKS).map(([key]) => `preset:${key}`);
        case 'npc':
          return Object.keys(NPC_DECKS).map(name => `npc:${name}`);
        case 'mydeck':
          return userDecks?.map((_, idx) => `mydeck:${idx}`) || [];
        default:
          return [];
      }
    })();
    if (options.length > 0) {
      setDeck(options[0]);
    }
  };

  return (
    <div>
      <div style={styles.row}>
        <span style={styles.label}>{playerLabel} デッキ:</span>
      </div>
      {/* カテゴリタブ */}
      <div style={styles.deckCategoryTabs}>
        {DECK_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            style={{
              ...styles.deckCategoryTab,
              ...(deckCategory === cat.id ? styles.deckCategoryTabActive : {}),
            }}
            onClick={() => handleCategoryChange(cat.id)}
          >
            {cat.label}
          </button>
        ))}
      </div>
      {/* デッキ選択 */}
      <div style={{ marginBottom: '12px' }}>
        <select
          style={{ ...styles.select, width: '100%' }}
          value={deck}
          onChange={(e) => setDeck(e.target.value)}
        >
          {deckOptions.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
            >
              {opt.label}
              {opt.owner && ` (${opt.owner})`}
              {opt.cardCount !== undefined && ` [${opt.cardCount}枚]`}
            </option>
          ))}
        </select>
      </div>
      {/* 難易度選択 */}
      <div style={styles.row}>
        <span style={styles.label}>{playerLabel} 難易度:</span>
        <select
          style={styles.select}
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
        >
          {DIFFICULTIES.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

// 設定パネル
const SettingsPanel = ({
  p1DeckCategory, setP1DeckCategory,
  p1Deck, setP1Deck,
  p2DeckCategory, setP2DeckCategory,
  p2Deck, setP2Deck,
  p1Difficulty, setP1Difficulty,
  p2Difficulty, setP2Difficulty,
  maxTurns, setMaxTurns,
  batchMode, setBatchMode,
  batchCount, setBatchCount,
  isRunning, onRun,
  isMobile,
  userDecks,
}) => {
  return (
    <div style={styles.panel}>
      <div style={styles.panelTitle}>⚙️ 設定</div>

      {/* モード切り替え */}
      <div style={styles.modeToggle}>
        <button
          style={{
            ...styles.modeButton,
            ...(!batchMode ? styles.modeButtonActive : {}),
          }}
          onClick={() => setBatchMode(false)}
        >
          🔍 詳細分析（1回）
        </button>
        <button
          style={{
            ...styles.modeButton,
            ...(batchMode ? styles.modeButtonActive : {}),
          }}
          onClick={() => setBatchMode(true)}
        >
          📊 統計分析（複数回）
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '16px',
      }}>
        {/* P1設定 */}
        <DeckSelector
          playerLabel="P1"
          deckCategory={p1DeckCategory}
          setDeckCategory={setP1DeckCategory}
          deck={p1Deck}
          setDeck={setP1Deck}
          difficulty={p1Difficulty}
          setDifficulty={setP1Difficulty}
          userDecks={userDecks}
        />

        {/* P2設定 */}
        <DeckSelector
          playerLabel="P2"
          deckCategory={p2DeckCategory}
          setDeckCategory={setP2DeckCategory}
          deck={p2Deck}
          setDeck={setP2Deck}
          difficulty={p2Difficulty}
          setDifficulty={setP2Difficulty}
          userDecks={userDecks}
        />
      </div>

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <div style={styles.row}>
          <span style={styles.label}>最大ターン:</span>
          <input
            type="number"
            style={styles.input}
            value={maxTurns}
            onChange={(e) => setMaxTurns(Math.max(5, Math.min(50, parseInt(e.target.value) || 15)))}
            min={5}
            max={50}
          />
        </div>

        {batchMode && (
          <div style={styles.row}>
            <span style={styles.label}>試行回数:</span>
            <input
              type="number"
              style={styles.input}
              value={batchCount}
              onChange={(e) => setBatchCount(Math.max(5, Math.min(1000, parseInt(e.target.value) || 20)))}
              min={5}
              max={1000}
            />
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center' }}>
        <button
          style={{
            ...styles.runButton,
            ...(isRunning ? styles.runButtonDisabled : {}),
          }}
          onClick={onRun}
          disabled={isRunning}
        >
          {isRunning ? '⏳ 実行中...' : batchMode ? `▶ ${batchCount}回シミュレーション` : '▶ シミュレーション実行'}
        </button>
      </div>
    </div>
  );
};

// バッチ結果サマリー（複数回シミュレーション用）
const BatchResultSummary = ({ result, p1DeckName, p2DeckName }) => {
  if (!result) return null;

  const { p1Wins, p2Wins, draws, games, avgTurns, avgDuration, firstPlayerStats, turnDistribution, lifeStats, spStats, setsunaStats, cardStats, totalGames: resultTotalGames } = result;
  const totalGames = games?.length || 0;
  const p1WinRate = totalGames > 0 ? (p1Wins / totalGames * 100) : 0;
  const p2WinRate = totalGames > 0 ? (p2Wins / totalGames * 100) : 0;
  const drawRate = totalGames > 0 ? (draws / totalGames * 100) : 0;
  const firstWinRate = (firstPlayerStats?.firstWinRate || 0) * 100;
  const secondWinRate = (firstPlayerStats?.secondWinRate || 0) * 100;

  // AI分析用にMarkdown形式で結果をコピー
  const copyResultsForAI = () => {
    // ターン分布の統計
    const turnKeys = Object.keys(turnDistribution || {}).map(Number).sort((a, b) => a - b);
    const turnMin = turnKeys[0] || 0;
    const turnMax = turnKeys[turnKeys.length - 1] || 0;
    const turnMode = turnKeys.reduce((a, b) =>
      (turnDistribution[a] || 0) > (turnDistribution[b] || 0) ? a : b, turnKeys[0]) || 0;

    const markdown = `## シミュレーション結果
- P1デッキ: ${p1DeckName || '不明'}
- P2デッキ: ${p2DeckName || '不明'}
- 試行回数: ${totalGames}回

## 勝率
- P1勝利: ${p1WinRate.toFixed(1)}% (${p1Wins}勝)
- P2勝利: ${p2WinRate.toFixed(1)}% (${p2Wins}勝)
- 引分: ${drawRate.toFixed(1)}% (${draws}回)

## 先攻/後攻
- 先攻勝率: ${firstWinRate.toFixed(1)}% (${firstPlayerStats?.firstWins || 0}勝)
- 後攻勝率: ${secondWinRate.toFixed(1)}% (${firstPlayerStats?.secondWins || 0}勝)
- P1先攻時勝率: ${((firstPlayerStats?.p1WinRateWhenFirst || 0) * 100).toFixed(1)}% (${firstPlayerStats?.p1WinsWhenFirst || 0}/${firstPlayerStats?.p1First || 0})
- P1後攻時勝率: ${((firstPlayerStats?.p1WinRateWhenSecond || 0) * 100).toFixed(1)}% (${firstPlayerStats?.p1WinsWhenSecond || 0}/${firstPlayerStats?.p2First || 0})
- P2先攻時勝率: ${((firstPlayerStats?.p2WinRateWhenFirst || 0) * 100).toFixed(1)}% (${firstPlayerStats?.p2WinsWhenFirst || 0}/${firstPlayerStats?.p2First || 0})
- P2後攻時勝率: ${((firstPlayerStats?.p2WinRateWhenSecond || 0) * 100).toFixed(1)}% (${firstPlayerStats?.p2WinsWhenSecond || 0}/${firstPlayerStats?.p1First || 0})

## ターン分布
- 平均: ${(avgTurns || 0).toFixed(1)}ターン
- 最頻値: ${turnMode}ターン
- 範囲: ${turnMin}-${turnMax}ターン
- 分布: ${turnKeys.map(t => `${t}T:${turnDistribution[t]}`).join(', ')}

## 勝者ライフ
- 平均残りライフ: ${(lifeStats?.avgWinnerLife || 0).toFixed(0)}
- ライフ分布: ${Object.entries(lifeStats?.winnerLifeDistribution || {}).sort((a, b) => Number(a[0]) - Number(b[0])).map(([k, v]) => `${k}-${Number(k) + 999}:${v}`).join(', ')}

## SP効率
- P1: 効率${(spStats?.p1Efficiency || 0).toFixed(2)} (使用${(spStats?.avgP1CardsUsed || 0).toFixed(1)}枚/消費${(spStats?.avgP1RestedSP || 0).toFixed(1)}SP, 残り${(spStats?.avgP1ActiveSP || 0).toFixed(1)}SP)
- P2: 効率${(spStats?.p2Efficiency || 0).toFixed(2)} (使用${(spStats?.avgP2CardsUsed || 0).toFixed(1)}枚/消費${(spStats?.avgP2RestedSP || 0).toFixed(1)}SP, 残り${(spStats?.avgP2ActiveSP || 0).toFixed(1)}SP)

## 刹那詠唱統計
- P1: 発動率${setsunaStats?.p1?.opportunities > 0 ? ((setsunaStats.p1.activations / setsunaStats.p1.opportunities) * 100).toFixed(1) : '0.0'}% (${setsunaStats?.p1?.activations || 0}/${setsunaStats?.p1?.opportunities || 0})
- P2: 発動率${setsunaStats?.p2?.opportunities > 0 ? ((setsunaStats.p2.activations / setsunaStats.p2.opportunities) * 100).toFixed(1) : '0.0'}% (${setsunaStats?.p2?.activations || 0}/${setsunaStats?.p2?.opportunities || 0})
${cardStats ? `
## P1カード統計
| カード名 | 属性 | コスト | プレイ回数 | 登場率 | プレイ時勝率 |
|---------|------|-------|----------|-------|------------|
${(cardStats.p1 || []).map(c => `| ${c.name} | ${c.attribute || '-'} | ${c.cost} | ${c.plays}回 | ${(c.playRate * 100).toFixed(0)}% | ${(c.winRate * 100).toFixed(1)}% |`).join('\n')}

## P2カード統計
| カード名 | 属性 | コスト | プレイ回数 | 登場率 | プレイ時勝率 |
|---------|------|-------|----------|-------|------------|
${(cardStats.p2 || []).map(c => `| ${c.name} | ${c.attribute || '-'} | ${c.cost} | ${c.plays}回 | ${(c.playRate * 100).toFixed(0)}% | ${(c.winRate * 100).toFixed(1)}% |`).join('\n')}
` : ''}`;

    navigator.clipboard.writeText(markdown).then(() => {
      alert('結果をクリップボードにコピーしました');
    }).catch(err => {
      console.error('コピー失敗:', err);
      alert('コピーに失敗しました');
    });
  };

  return (
    <div style={styles.panel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={styles.panelTitle}>📊 統計結果（{totalGames}回シミュレーション）</div>
        <button
          onClick={copyResultsForAI}
          style={{
            padding: '6px 12px',
            background: 'linear-gradient(135deg, #2d5a4a, #1a3a2a)',
            border: '1px solid #4ade80',
            borderRadius: '6px',
            color: '#4ade80',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          📋 AI分析用にコピー
        </button>
      </div>

      {/* P1 vs P2 勝率バー */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '6px' }}>P1 vs P2 勝率:</div>
        <div style={styles.winRateBar}>
          {p1WinRate > 0 && (
            <div style={styles.winRateSegment('#4a90d9', p1WinRate)}>
              {p1WinRate >= 10 && `P1 ${p1WinRate.toFixed(0)}%`}
            </div>
          )}
          {drawRate > 0 && (
            <div style={styles.winRateSegment('#888', drawRate)}>
              {drawRate >= 10 && `引分 ${drawRate.toFixed(0)}%`}
            </div>
          )}
          {p2WinRate > 0 && (
            <div style={styles.winRateSegment('#d94a4a', p2WinRate)}>
              {p2WinRate >= 10 && `P2 ${p2WinRate.toFixed(0)}%`}
            </div>
          )}
        </div>
      </div>

      {/* 先攻/後攻 勝率バー */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '6px' }}>先攻 vs 後攻 勝率:</div>
        <div style={styles.winRateBar}>
          {firstWinRate > 0 && (
            <div style={styles.winRateSegment('#ffd700', firstWinRate)}>
              {firstWinRate >= 10 && `先攻 ${firstWinRate.toFixed(0)}%`}
            </div>
          )}
          {secondWinRate > 0 && (
            <div style={styles.winRateSegment('#9d4ce6', secondWinRate)}>
              {secondWinRate >= 10 && `後攻 ${secondWinRate.toFixed(0)}%`}
            </div>
          )}
        </div>
        <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
          ※引き分けを除く（先攻: {firstPlayerStats?.firstWins || 0}勝、後攻: {firstPlayerStats?.secondWins || 0}勝）
        </div>
      </div>

      {/* P1/P2 先攻・後攻別勝率 */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>プレイヤー別 先攻/後攻 勝率:</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {/* P1 */}
          <div style={{ background: 'rgba(74, 144, 217, 0.15)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(74, 144, 217, 0.3)' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#4a90d9', marginBottom: '6px' }}>P1</div>
            <div style={{ fontSize: '12px', color: '#c0c0c0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>先攻時:</span>
                <span style={{ color: '#ffd700', fontWeight: 'bold' }}>
                  {((firstPlayerStats?.p1WinRateWhenFirst || 0) * 100).toFixed(0)}%
                  <span style={{ color: '#888', fontWeight: 'normal' }}> ({firstPlayerStats?.p1WinsWhenFirst || 0}/{firstPlayerStats?.p1First || 0})</span>
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>後攻時:</span>
                <span style={{ color: '#9d4ce6', fontWeight: 'bold' }}>
                  {((firstPlayerStats?.p1WinRateWhenSecond || 0) * 100).toFixed(0)}%
                  <span style={{ color: '#888', fontWeight: 'normal' }}> ({firstPlayerStats?.p1WinsWhenSecond || 0}/{firstPlayerStats?.p2First || 0})</span>
                </span>
              </div>
            </div>
          </div>
          {/* P2 */}
          <div style={{ background: 'rgba(217, 74, 74, 0.15)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(217, 74, 74, 0.3)' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#d94a4a', marginBottom: '6px' }}>P2</div>
            <div style={{ fontSize: '12px', color: '#c0c0c0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>先攻時:</span>
                <span style={{ color: '#ffd700', fontWeight: 'bold' }}>
                  {((firstPlayerStats?.p2WinRateWhenFirst || 0) * 100).toFixed(0)}%
                  <span style={{ color: '#888', fontWeight: 'normal' }}> ({firstPlayerStats?.p2WinsWhenFirst || 0}/{firstPlayerStats?.p2First || 0})</span>
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>後攻時:</span>
                <span style={{ color: '#9d4ce6', fontWeight: 'bold' }}>
                  {((firstPlayerStats?.p2WinRateWhenSecond || 0) * 100).toFixed(0)}%
                  <span style={{ color: '#888', fontWeight: 'normal' }}> ({firstPlayerStats?.p2WinsWhenSecond || 0}/{firstPlayerStats?.p1First || 0})</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 統計グリッド */}
      <div style={styles.statsGrid}>
        <div style={styles.statItem}>
          <div style={styles.statValue}>{p1Wins}</div>
          <div style={styles.statLabel}>P1 勝利</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statValue}>{p2Wins}</div>
          <div style={styles.statLabel}>P2 勝利</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statValue}>{draws}</div>
          <div style={styles.statLabel}>引き分け</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statValue}>{(avgTurns || 0).toFixed(1)}</div>
          <div style={styles.statLabel}>平均ターン</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statValue}>{(avgDuration || 0).toFixed(1)}ms</div>
          <div style={styles.statLabel}>平均実行時間</div>
        </div>
        <div style={styles.statItem}>
          <div style={{ ...styles.statValue, color: firstWinRate > 50 ? '#ffd700' : '#9d4ce6' }}>
            {firstWinRate > secondWinRate ? '先攻' : secondWinRate > firstWinRate ? '後攻' : '同等'}
          </div>
          <div style={styles.statLabel}>有利</div>
        </div>
      </div>

      {/* ターン分布 */}
      {result.turnDistribution && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>📊 ターン分布:</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '80px', background: 'rgba(0,0,0,0.2)', padding: '8px', borderRadius: '8px' }}>
            {(() => {
              const dist = result.turnDistribution;
              const maxTurn = Math.max(...Object.keys(dist).map(Number));
              const minTurn = Math.min(...Object.keys(dist).map(Number));
              const maxCount = Math.max(...Object.values(dist));
              const bars = [];
              for (let t = minTurn; t <= maxTurn; t++) {
                const count = dist[t] || 0;
                const height = maxCount > 0 ? (count / maxCount) * 60 : 0;
                bars.push(
                  <div key={t} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: '20px' }}>
                    <div style={{ fontSize: '9px', color: '#888', marginBottom: '2px' }}>{count > 0 ? count : ''}</div>
                    <div style={{
                      width: '100%',
                      maxWidth: '30px',
                      height: `${height}px`,
                      background: count > 0 ? 'linear-gradient(to top, #4a90d9, #6bb5ff)' : 'transparent',
                      borderRadius: '2px 2px 0 0',
                    }} />
                    <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>{t}</div>
                  </div>
                );
              }
              return bars;
            })()}
          </div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', textAlign: 'center' }}>
            ターン数
          </div>
        </div>
      )}

      {/* 残りライフ分布 */}
      {result.lifeStats && result.lifeStats.decidedGamesCount > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>❤️ 勝者残りライフ:</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>平均残りライフ</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4ade80' }}>
                {(result.lifeStats.avgWinnerLife || 0).toFixed(0)}
              </div>
            </div>
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>ライフ分布</div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {Object.entries(result.lifeStats.winnerLifeDistribution || {})
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .map(([bucket, count]) => (
                    <span key={bucket} style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      background: 'rgba(74, 222, 128, 0.2)',
                      borderRadius: '4px',
                      color: '#4ade80',
                    }}>
                      {bucket}-{Number(bucket) + 999}: {count}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SP効率 */}
      {result.spStats && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>⚡ SP効率:</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* P1 */}
            <div style={{ background: 'rgba(74, 144, 217, 0.15)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(74, 144, 217, 0.3)' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#4a90d9', marginBottom: '6px' }}>P1</div>
              <div style={{ fontSize: '11px', color: '#c0c0c0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>使用カード数:</span>
                  <span style={{ fontWeight: 'bold' }}>{(result.spStats.avgP1CardsUsed || 0).toFixed(1)}枚</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>消費SP:</span>
                  <span>{(result.spStats.avgP1RestedSP || 0).toFixed(1)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>残りSP:</span>
                  <span>{(result.spStats.avgP1ActiveSP || 0).toFixed(1)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '4px', marginTop: '4px' }}>
                  <span>効率:</span>
                  <span style={{ color: '#ffd700', fontWeight: 'bold' }}>{(result.spStats.p1Efficiency || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
            {/* P2 */}
            <div style={{ background: 'rgba(217, 74, 74, 0.15)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(217, 74, 74, 0.3)' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#d94a4a', marginBottom: '6px' }}>P2</div>
              <div style={{ fontSize: '11px', color: '#c0c0c0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>使用カード数:</span>
                  <span style={{ fontWeight: 'bold' }}>{(result.spStats.avgP2CardsUsed || 0).toFixed(1)}枚</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>消費SP:</span>
                  <span>{(result.spStats.avgP2RestedSP || 0).toFixed(1)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>残りSP:</span>
                  <span>{(result.spStats.avgP2ActiveSP || 0).toFixed(1)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '4px', marginTop: '4px' }}>
                  <span>効率:</span>
                  <span style={{ color: '#ffd700', fontWeight: 'bold' }}>{(result.spStats.p2Efficiency || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
            ※効率 = 使用カード数 ÷ 消費SP（高いほど低コストカードを多く使用）
          </div>
        </div>
      )}

      {/* 刹那詠唱統計 */}
      {setsunaStats && (setsunaStats.p1.opportunities > 0 || setsunaStats.p2.opportunities > 0) && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>⚡ 刹那詠唱統計:</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* P1 */}
            <div style={{ background: 'rgba(74, 144, 217, 0.15)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(74, 144, 217, 0.3)' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#4a90d9', marginBottom: '6px' }}>P1</div>
              <div style={{ fontSize: '11px', color: '#c0c0c0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>発動機会:</span>
                  <span style={{ fontWeight: 'bold' }}>{setsunaStats.p1.opportunities}回</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>発動回数:</span>
                  <span style={{ fontWeight: 'bold' }}>{setsunaStats.p1.activations}回</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '4px', marginTop: '4px' }}>
                  <span>発動率:</span>
                  <span style={{ color: '#ffd700', fontWeight: 'bold' }}>
                    {setsunaStats.p1.opportunities > 0
                      ? ((setsunaStats.p1.activations / setsunaStats.p1.opportunities) * 100).toFixed(1)
                      : '0.0'}%
                  </span>
                </div>
              </div>
            </div>
            {/* P2 */}
            <div style={{ background: 'rgba(217, 74, 74, 0.15)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(217, 74, 74, 0.3)' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#d94a4a', marginBottom: '6px' }}>P2</div>
              <div style={{ fontSize: '11px', color: '#c0c0c0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>発動機会:</span>
                  <span style={{ fontWeight: 'bold' }}>{setsunaStats.p2.opportunities}回</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>発動回数:</span>
                  <span style={{ fontWeight: 'bold' }}>{setsunaStats.p2.activations}回</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '4px', marginTop: '4px' }}>
                  <span>発動率:</span>
                  <span style={{ color: '#ffd700', fontWeight: 'bold' }}>
                    {setsunaStats.p2.opportunities > 0
                      ? ((setsunaStats.p2.activations / setsunaStats.p2.opportunities) * 100).toFixed(1)
                      : '0.0'}%
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
            ※発動機会 = 刹那詠唱可能なカードがある状態でチェーンポイントに到達した回数
          </div>
        </div>
      )}

      {/* カード統計 */}
      {cardStats && (cardStats.p1?.length > 0 || cardStats.p2?.length > 0) && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>🃏 カード統計:</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* P1カード統計 */}
            <div style={{ background: 'rgba(74, 144, 217, 0.15)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(74, 144, 217, 0.3)' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#4a90d9', marginBottom: '8px' }}>P1</div>
              <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                      <th style={{ padding: '4px', color: '#888', textAlign: 'left' }}>カード</th>
                      <th style={{ padding: '4px', color: '#888', textAlign: 'center' }}>回数</th>
                      <th style={{ padding: '4px', color: '#888', textAlign: 'center' }}>登場率</th>
                      <th style={{ padding: '4px', color: '#888', textAlign: 'center' }}>勝率</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cardStats.p1 || []).slice(0, 15).map((card, idx) => (
                      <tr key={card.id || idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '3px', color: '#e0e0e0', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.name}</td>
                        <td style={{ padding: '3px', color: '#60a5fa', textAlign: 'center' }}>{card.plays}</td>
                        <td style={{ padding: '3px', color: '#a0a0a0', textAlign: 'center' }}>{(card.playRate * 100).toFixed(0)}%</td>
                        <td style={{ padding: '3px', color: card.winRate >= 0.55 ? '#4ade80' : card.winRate <= 0.45 ? '#f87171' : '#e0e0e0', textAlign: 'center', fontWeight: 'bold' }}>{(card.winRate * 100).toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(cardStats.p1?.length || 0) > 15 && (
                  <div style={{ fontSize: '9px', color: '#666', textAlign: 'center', marginTop: '4px' }}>
                    ...他 {cardStats.p1.length - 15} 枚
                  </div>
                )}
              </div>
            </div>
            {/* P2カード統計 */}
            <div style={{ background: 'rgba(217, 74, 74, 0.15)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(217, 74, 74, 0.3)' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#d94a4a', marginBottom: '8px' }}>P2</div>
              <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                      <th style={{ padding: '4px', color: '#888', textAlign: 'left' }}>カード</th>
                      <th style={{ padding: '4px', color: '#888', textAlign: 'center' }}>回数</th>
                      <th style={{ padding: '4px', color: '#888', textAlign: 'center' }}>登場率</th>
                      <th style={{ padding: '4px', color: '#888', textAlign: 'center' }}>勝率</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cardStats.p2 || []).slice(0, 15).map((card, idx) => (
                      <tr key={card.id || idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '3px', color: '#e0e0e0', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.name}</td>
                        <td style={{ padding: '3px', color: '#60a5fa', textAlign: 'center' }}>{card.plays}</td>
                        <td style={{ padding: '3px', color: '#a0a0a0', textAlign: 'center' }}>{(card.playRate * 100).toFixed(0)}%</td>
                        <td style={{ padding: '3px', color: card.winRate >= 0.55 ? '#4ade80' : card.winRate <= 0.45 ? '#f87171' : '#e0e0e0', textAlign: 'center', fontWeight: 'bold' }}>{(card.winRate * 100).toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(cardStats.p2?.length || 0) > 15 && (
                  <div style={{ fontSize: '9px', color: '#666', textAlign: 'center', marginTop: '4px' }}>
                    ...他 {cardStats.p2.length - 15} 枚
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
            登場率 = そのカードが場または墓地に登場した試合の割合 / 勝率 = 登場試合のうち勝利した割合
          </div>
        </div>
      )}

      {/* 試合一覧 */}
      <div style={{ marginTop: '16px', fontSize: '14px', color: '#a0a0a0' }}>各試合の結果:</div>
      <div style={styles.gameList}>
        {result.games?.slice(0, 50).map((game, idx) => (
          <div key={idx} style={styles.gameItem}>
            <span style={{ color: '#888' }}>#{idx + 1}</span>
            <span style={{ color: game.winner === 1 ? '#4a90d9' : game.winner === 2 ? '#d94a4a' : '#888' }}>
              {game.winner ? `P${game.winner}勝利` : '引分'}
            </span>
            <span style={{ color: '#aaa' }}>
              {game.firstPlayer === game.winner ? '(先攻勝)' : game.winner ? '(後攻勝)' : ''}
            </span>
            <span style={{ color: '#666' }}>{game.turns}ターン</span>
          </div>
        ))}
        {result.games?.length > 50 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '8px' }}>
            ...他 {result.games.length - 50} 試合
          </div>
        )}
      </div>
    </div>
  );
};

// 結果サマリー（詳細分析用）
const ResultSummary = ({ result, p1DeckName, p2DeckName, p1Difficulty, p2Difficulty }) => {
  if (!result) return null;

  const decisionCounts = result.aiDecisions.reduce((acc, d) => {
    acc[d.category] = (acc[d.category] || 0) + 1;
    return acc;
  }, {});

  // AI分析用にMarkdown形式で結果をコピー（詳細ログ含む）
  const copyDetailedResultForAI = () => {
    let markdown = `## AI思考分析 - 詳細結果\n\n`;
    markdown += `### 対戦情報\n`;
    markdown += `- P1: ${p1DeckName || 'デッキ1'} (${p1Difficulty || 'normal'})\n`;
    markdown += `- P2: ${p2DeckName || 'デッキ2'} (${p2Difficulty || 'normal'})\n`;
    markdown += `- 先攻: P${result.firstPlayer}\n\n`;

    markdown += `### 結果サマリー\n`;
    markdown += `| 項目 | 値 |\n`;
    markdown += `|------|----|\n`;
    markdown += `| 勝者 | ${result.winner ? `P${result.winner}` : '引き分け'} |\n`;
    markdown += `| ターン数 | ${result.turns} |\n`;
    markdown += `| P1残りライフ | ${result.p1Life} |\n`;
    markdown += `| P2残りライフ | ${result.p2Life} |\n`;
    markdown += `| 実行時間 | ${result.duration}ms |\n\n`;

    markdown += `### 判断回数\n`;
    Object.entries(decisionCounts).forEach(([cat, count]) => {
      markdown += `- ${getCategoryLabel(cat)}: ${count}回\n`;
    });
    markdown += `\n`;

    // 詳細ログを追加
    markdown += `### 詳細ログ\n\n`;

    // ターンごとにグループ化
    const groupedByTurn = result.aiDecisions.reduce((acc, d) => {
      if (!acc[d.turn]) acc[d.turn] = { 1: [], 2: [] };
      acc[d.turn][d.player].push(d);
      return acc;
    }, {});

    Object.entries(groupedByTurn).forEach(([turn, players]) => {
      const displayOrder = result.firstPlayer === 2 ? [2, 1] : [1, 2];
      markdown += `#### ターン ${turn}\n\n`;

      displayOrder.forEach(player => {
        if (players[player].length === 0) return;
        const isFirst = player === result.firstPlayer;
        markdown += `**P${player}${isFirst ? '（先攻）' : '（後攻）'}**\n\n`;

        players[player].forEach(d => {
          if (d.category === 'status') {
            // 状況ログ
            markdown += `📋 **ターン開始時の状況**\n`;
            if (d.details) {
              markdown += `- SP: ${d.details.activeSP || 0}(+${d.details.bonusSP || 0}ボーナス)\n`;
              markdown += `- ライフ: ${d.details.life} / 相手: ${d.details.opponentLife}\n`;
              markdown += `- 手札: ${d.details.handCount || 0}枚\n`;
              if (d.details.myField?.length > 0) {
                markdown += `- 自分フィールド (${d.details.myField.length}体):\n`;
                d.details.myField.forEach(m => {
                  markdown += `  - [${m.slot}] ${m.name} ATK:${m.currentAttack} HP:${m.currentHp} チャージ:${m.charges}\n`;
                });
              }
              if (d.details.opponentField?.length > 0) {
                markdown += `- 相手フィールド (${d.details.opponentField.length}体):\n`;
                d.details.opponentField.forEach(m => {
                  markdown += `  - [${m.slot}] ${m.name} ATK:${m.currentAttack} HP:${m.currentHp}\n`;
                });
              }
            }
          } else {
            // 通常の判断ログ
            const actionSymbol = d.action === 'skipped' || d.action === 'none' ? '✗' : '✓';
            markdown += `[${d.phase}] ${actionSymbol} **${getCategoryLabel(d.category)}**: ${getActionLabel(d.action)}\n`;

            if (d.details) {
              if (d.details.cardName) {
                markdown += `- カード: ${d.details.cardName}\n`;
              }
              if (d.details.cost !== undefined) {
                markdown += `- コスト: ${d.details.cost}`;
                if (d.details.attack !== undefined) markdown += ` ATK: ${d.details.attack}`;
                if (d.details.hp !== undefined) markdown += ` HP: ${d.details.hp}`;
                markdown += `\n`;
              }
              if (d.details.slot !== undefined) {
                markdown += `- スロット: ${d.details.slot}\n`;
              }
              if (d.details.skillType) {
                markdown += `- タイプ: ${d.details.skillType === 'basic' ? '基本技' : '上級技'}\n`;
              }
              if (d.details.targetName) {
                markdown += `- ターゲット: ${d.details.targetName}\n`;
              }
              if (d.details.reason) {
                markdown += `- 理由: ${d.details.reason}\n`;
              }
              if (d.details.alternatives?.length > 0) {
                markdown += `- 候補: ${d.details.alternatives.slice(0, 5).map(a => typeof a === 'string' ? a : a.name).join(', ')}`;
                if (d.details.alternatives.length > 5) markdown += ` 他${d.details.alternatives.length - 5}枚`;
                markdown += `\n`;
              }
            }
          }
          markdown += `\n`;
        });
      });
    });

    navigator.clipboard.writeText(markdown).then(() => {
      alert('詳細結果をクリップボードにコピーしました');
    }).catch(err => {
      console.error('コピー失敗:', err);
      alert('コピーに失敗しました');
    });
  };

  return (
    <div style={styles.panel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={styles.panelTitle}>📊 結果サマリー</div>
        <button
          onClick={copyDetailedResultForAI}
          style={{
            padding: '6px 12px',
            background: 'linear-gradient(135deg, #2d5a4a, #1a3a2a)',
            border: '1px solid #4a8a6a',
            borderRadius: '4px',
            color: '#a0d0b0',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          📋 AI分析用にコピー
        </button>
      </div>

      <div style={styles.summaryGrid}>
        <div style={styles.summaryItem}>
          <div style={styles.summaryValue}>
            {result.winner ? `P${result.winner}` : '引分'}
          </div>
          <div style={styles.summaryLabel}>勝者</div>
        </div>
        <div style={styles.summaryItem}>
          <div style={styles.summaryValue}>{result.turns}</div>
          <div style={styles.summaryLabel}>ターン数</div>
        </div>
        <div style={styles.summaryItem}>
          <div style={styles.summaryValue}>{result.p1Life}</div>
          <div style={styles.summaryLabel}>P1 Life</div>
        </div>
        <div style={styles.summaryItem}>
          <div style={styles.summaryValue}>{result.p2Life}</div>
          <div style={styles.summaryLabel}>P2 Life</div>
        </div>
        <div style={styles.summaryItem}>
          <div style={styles.summaryValue}>{result.duration}ms</div>
          <div style={styles.summaryLabel}>実行時間</div>
        </div>
      </div>

      <div style={{ marginTop: '16px' }}>
        <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>判断回数:</div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {Object.entries(decisionCounts).map(([cat, count]) => (
            <span key={cat} style={{ color: '#c0c0c0' }}>
              {getCategoryLabel(cat)}: <strong style={{ color: '#ffd700' }}>{count}</strong>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// 詳細ログ
const DetailLog = ({ result, activeTab, turnFilter, playerFilter }) => {
  const filteredDecisions = useMemo(() => {
    if (!result) return [];

    let decisions = result.aiDecisions;

    // タブでフィルター
    if (activeTab !== 'all') {
      // phaseCardタブではphaseCardChargeも含める
      if (activeTab === 'phaseCard') {
        decisions = decisions.filter(d => d.category === 'phaseCard' || d.category === 'phaseCardCharge');
      } else {
        decisions = decisions.filter(d => d.category === activeTab);
      }
    }

    // ターンでフィルター
    if (turnFilter !== 'all') {
      decisions = decisions.filter(d => d.turn === parseInt(turnFilter));
    }

    // プレイヤーでフィルター
    if (playerFilter !== 'all') {
      decisions = decisions.filter(d => d.player === parseInt(playerFilter));
    }

    return decisions;
  }, [result, activeTab, turnFilter, playerFilter]);

  if (!result) {
    return (
      <div style={styles.panel}>
        <div style={styles.panelTitle}>📝 詳細ログ</div>
        <div style={{ color: '#808080', textAlign: 'center', padding: '40px' }}>
          シミュレーションを実行してください
        </div>
      </div>
    );
  }

  // ターンごとにグループ化
  const groupedByTurn = filteredDecisions.reduce((acc, d) => {
    if (!acc[d.turn]) acc[d.turn] = { 1: [], 2: [] };
    acc[d.turn][d.player].push(d);
    return acc;
  }, {});

  return (
    <div style={styles.panel}>
      <div style={styles.panelTitle}>📝 詳細ログ</div>

      <div style={styles.logContainer}>
        {Object.entries(groupedByTurn).map(([turn, players]) => {
          // 先攻プレイヤーから表示（result.firstPlayerを使用）
          const displayOrder = result.firstPlayer === 2 ? [2, 1] : [1, 2];

          return (
            <div key={turn}>
              <div style={styles.logTurn}>========== ターン {turn} ==========</div>

              {displayOrder.map(player => {
                if (playerFilter !== 'all' && player !== parseInt(playerFilter)) return null;
                if (players[player].length === 0) return null;

                const isFirstPlayer = player === result.firstPlayer;

                return (
                  <div key={player}>
                    <div style={styles.logPlayer}>--- P{player} のターン{isFirstPlayer ? '（先攻）' : '（後攻）'} ---</div>
                    {players[player].map((d, idx) => (
                      <div key={idx} style={styles.logEntry}>
                        <div>
                          {d.category === 'status' ? (
                            // 状況ログは特別な表示
                            <span style={{ color: '#88ccff', fontWeight: 'bold' }}>
                              📋 ターン開始時の状況
                            </span>
                          ) : (
                            <>
                              <span style={styles.logPhase}>[{d.phase}] </span>
                              <span style={styles.logAction(d.action !== 'skipped' && d.action !== 'none')}>
                                {d.action === 'skipped' || d.action === 'none' ? '✗' : '✓'} {getCategoryLabel(d.category)}: {getActionLabel(d.action)}
                              </span>
                            </>
                          )}
                        </div>
                        {renderDecisionDetails(d)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}

        {filteredDecisions.length === 0 && (
          <div style={{ color: '#808080', textAlign: 'center', padding: '20px' }}>
            該当するログがありません
          </div>
        )}
      </div>
    </div>
  );
};

// 判断詳細をレンダリング
const renderDecisionDetails = (decision) => {
  const { category, details } = decision;

  switch (category) {
    case 'magic':
      if (details.cardName) {
        return (
          <div style={styles.logDetail}>
            カード: {details.cardName}<br />
            {details.effectType && <>効果タイプ: {details.effectType}<br /></>}
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      if (details.alternatives?.length > 0) {
        return (
          <div style={styles.logDetail}>
            候補: {details.alternatives.length}枚<br />
            {details.alternatives.slice(0, 3).map((alt, i) => (
              <span key={i}>- {alt.name || alt} ({alt.effectType || ''})<br /></span>
            ))}
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      return null;

    case 'summon':
      if (details.cardName) {
        return (
          <div style={styles.logDetail}>
            カード: {details.cardName}<br />
            コスト: {details.cost} ATK: {details.attack} HP: {details.hp}<br />
            スロット: {details.slot}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      if (details.alternatives?.length > 0) {
        return (
          <div style={styles.logDetail}>
            候補: {details.alternatives.length}枚<br />
            SP: {details.activeSP}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      return null;

    case 'skill':
      if (details.cardName) {
        return (
          <div style={styles.logDetail}>
            カード: {details.cardName}<br />
            タイプ: {details.skillType === 'advanced' ? '上級技' : '基本技'}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      return null;

    case 'attack':
      if (details.attackerName) {
        return (
          <div style={styles.logDetail}>
            攻撃者: {details.attackerName} (ATK: {details.attackerAttack})<br />
            対象: {details.targetName}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      if (details.reason) {
        return <div style={styles.logDetail}>理由: {details.reason}</div>;
      }
      return null;

    case 'charge':
      if (details.cardName) {
        return (
          <div style={styles.logDetail}>
            カード: {details.cardName}<br />
            対象モンスター: {details.targetMonsterName}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      return null;

    case 'setsuna':
      if (details.cardName) {
        return (
          <div style={styles.logDetail}>
            カード: {details.cardName}<br />
            チェインポイント: {details.chainPoint}<br />
            コスト: {details.cost}<br />
          </div>
        );
      }
      if (details.availableCount !== undefined) {
        return (
          <div style={styles.logDetail}>
            発動可能数: {details.availableCount}枚<br />
            チェインポイント: {details.chainPoint}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      return null;

    case 'fieldCard':
      if (details.cardName) {
        return (
          <div style={styles.logDetail}>
            カード: {details.cardName}<br />
            コスト: {details.cost}<br />
            {details.effect && <>効果: {details.effect.substring(0, 50)}...<br /></>}
          </div>
        );
      }
      if (details.alternatives?.length > 0) {
        return (
          <div style={styles.logDetail}>
            候補: {details.alternatives.map(a => a.name || a).join(', ')}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      return null;

    case 'phaseCard':
      if (details.cardName) {
        return (
          <div style={styles.logDetail}>
            カード: {details.cardName}<br />
            コスト: {details.cost}<br />
            {details.effect && <>効果: {details.effect.substring(0, 50)}...<br /></>}
          </div>
        );
      }
      if (details.alternatives?.length > 0) {
        return (
          <div style={styles.logDetail}>
            候補: {details.alternatives.map(a => a.name || a).join(', ')}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      return null;

    case 'phaseCardCharge':
      if (details.cardName) {
        return (
          <div style={styles.logDetail}>
            チャージ: {details.cardName}<br />
            フェイズカード: {details.phaseCardName}<br />
            現在段階: {details.currentStage}<br />
            {details.reason && <>理由: {details.reason}</>}
          </div>
        );
      }
      return null;

    case 'phase':
      return (
        <div style={styles.logDetail}>
          {details.reason && <>理由: {details.reason}</>}
        </div>
      );

    case 'status':
      // ターン開始時の状況表示
      return (
        <div style={styles.logDetail}>
          <div style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#ffd700' }}>💰 リソース:</strong>{' '}
            Life: {details.life} | SP: {details.activeSP}/{details.activeSP + details.restedSP}{details.bonusSP > 0 ? ` +☆${details.bonusSP}` : ''} | 山札: {details.deckCount}枚 | 墓地: {details.graveyardCount}枚
          </div>
          <div style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#88ccff' }}>🃏 手札 ({details.handCount}枚):</strong>
            <div style={{ marginLeft: '12px', marginTop: '4px' }}>
              {details.hand?.map((card, i) => (
                <div key={i} style={{ fontSize: '11px', color: '#c0c0c0', marginBottom: '2px' }}>
                  {getTypeIcon(card.type)} {card.name}
                  {card.type === 'monster' && <span style={{ color: '#ff9999' }}> ATK:{card.attack} HP:{card.hp}</span>}
                  <span style={{ color: '#aaa' }}> (コスト{card.cost})</span>
                </div>
              ))}
            </div>
          </div>
          {details.myField?.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <strong style={{ color: '#6bff6b' }}>⚔️ 自分フィールド ({details.myField.length}体):</strong>
              <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                {details.myField.map((m, i) => (
                  <div key={i} style={{ fontSize: '11px', color: '#c0c0c0' }}>
                    [{m.slot}] {m.name} ATK:{m.currentAttack} HP:{m.currentHp} チャージ:{m.charges}
                  </div>
                ))}
              </div>
            </div>
          )}
          {details.opponentField?.length > 0 && (
            <div>
              <strong style={{ color: '#ff6b6b' }}>🛡️ 相手フィールド ({details.opponentField.length}体):</strong>
              <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                {details.opponentField.map((m, i) => (
                  <div key={i} style={{ fontSize: '11px', color: '#c0c0c0' }}>
                    [{m.slot}] {m.name} ATK:{m.currentAttack} HP:{m.currentHp}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );

    default:
      return details.reason ? (
        <div style={styles.logDetail}>理由: {details.reason}</div>
      ) : null;
  }
};

// ========================================
// 総当たり分析タブ
// ========================================

const RoundRobinTab = ({ allCards, playerData, isMobile }) => {
  // デッキリスト（プリセット + NPC + マイデッキ）
  const userDecks = playerData?.userDecks || [];

  const allDecks = useMemo(() => {
    const decks = [];

    // プリセットデッキ
    Object.entries(PREBUILT_DECKS).forEach(([id, deck]) => {
      decks.push({
        id: `preset:${id}`,
        name: deck.name,
        category: 'プリセット',
      });
    });

    // NPCデッキ
    Object.entries(NPC_DECKS).forEach(([id, deck]) => {
      decks.push({
        id: `npc:${id}`,
        name: deck.name,
        category: 'NPC',
      });
    });

    // マイデッキ
    userDecks.forEach((deck, index) => {
      decks.push({
        id: `mydeck:${index}`,
        name: deck.name,
        category: 'マイデッキ',
      });
    });

    return decks;
  }, [userDecks]);

  // 選択状態
  const [selectedDeckIds, setSelectedDeckIds] = useState([]);
  const [gamesPerMatch, setGamesPerMatch] = useState(20);
  const [difficulty, setDifficulty] = useState('hard');
  const [mirrorMatchOnly, setMirrorMatchOnly] = useState(false); // ミラーマッチモード
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);

  // 結果表示タブ: 'standings' = 順位表/相性, 'cardStats' = カード統計
  const [resultTab, setResultTab] = useState('standings');
  const [selectedStatsDeckId, setSelectedStatsDeckId] = useState(null);

  // デッキ選択のトグル
  const toggleDeck = (deckId) => {
    setSelectedDeckIds(prev =>
      prev.includes(deckId)
        ? prev.filter(id => id !== deckId)
        : [...prev, deckId]
    );
  };

  // 全選択/全解除
  const selectAll = () => setSelectedDeckIds(allDecks.map(d => d.id));
  const deselectAll = () => setSelectedDeckIds([]);

  // 組み合わせ数を計算
  // ミラーマッチモード: 各デッキ × 1 = n通り
  // 通常モード: n*(n-1)/2 通り
  const matchCount = mirrorMatchOnly
    ? selectedDeckIds.length
    : selectedDeckIds.length * (selectedDeckIds.length - 1) / 2;
  const totalGames = matchCount * gamesPerMatch;

  // デッキ構築（buildDeckFromSelectionと同じロジック）
  const buildDeck = (deckValue) => {
    const [category, id] = deckValue.split(':');

    switch (category) {
      case 'preset':
        return getDeckByName(id, allCards);

      case 'npc': {
        const npcDeck = NPC_DECKS[id];
        if (!npcDeck) throw new Error(`NPCデッキ「${id}」が見つかりません`);
        const cardIds = expandDeckToCardIds(npcDeck);
        const deck = cardIds.map(cardId => {
          const cardData = allCards.find(c => c.id === cardId);
          if (!cardData) return null;
          return { ...cardData, uniqueId: `${cardId}_${Math.random().toString(36).substr(2, 9)}` };
        }).filter(Boolean);
        return shuffle(deck);
      }

      case 'mydeck': {
        const deckIndex = parseInt(id);
        const userDeck = userDecks[deckIndex];
        if (!userDeck) throw new Error(`マイデッキが見つかりません`);
        const deck = [];
        for (const deckCard of userDeck.cards || []) {
          const cardData = allCards.find(c => c.id === deckCard.cardId);
          if (cardData) {
            deck.push({ ...cardData, uniqueId: `${cardData.id}_${Math.random().toString(36).substr(2, 9)}` });
          }
        }
        return shuffle(deck);
      }

      default:
        throw new Error(`不明なデッキカテゴリ: ${category}`);
    }
  };

  // デッキのカードID一覧を取得
  const getDeckCardIds = (deckValue) => {
    const [category, id] = deckValue.split(':');
    try {
      switch (category) {
        case 'preset': {
          const presetDeck = PREBUILT_DECKS[id];
          if (!presetDeck?.cards) return [];
          // カードIDをユニーク化してカウント
          const cardCount = {};
          presetDeck.cards.forEach(cardId => {
            cardCount[cardId] = (cardCount[cardId] || 0) + 1;
          });
          return Object.entries(cardCount).map(([cardId, count]) => ({ cardId, count }));
        }
        case 'npc': {
          const npcDeck = NPC_DECKS[id];
          if (!npcDeck?.cards) return [];
          return npcDeck.cards.map(entry => ({ cardId: entry.id, count: entry.count }));
        }
        case 'mydeck': {
          const deckIndex = parseInt(id);
          const userDeck = userDecks[deckIndex];
          if (!userDeck?.cards) return [];
          return userDeck.cards.map(entry => ({ cardId: entry.cardId, count: 1 }));
        }
        default:
          return [];
      }
    } catch {
      return [];
    }
  };

  // 総当たり実行
  const runRoundRobin = async () => {
    // ミラーマッチモード: 1つ以上、通常モード: 2つ以上
    const minDecks = mirrorMatchOnly ? 1 : 2;
    if (selectedDeckIds.length < minDecks) {
      alert(mirrorMatchOnly ? '1つ以上のデッキを選択してください' : '2つ以上のデッキを選択してください');
      return;
    }

    // 召喚酔い・後攻ボーナスSP・貫通ダメージ・孤軍奮闘・初期LP設定を取得
    const { summoningSickness, secondPlayerBonusSP, piercingDamage, loneWarriorRule, initialLifePoints } = getPlayerDeckSettings();

    setIsRunning(true);
    setProgress({ current: 0, total: matchCount });

    const resultMatrix = {};
    let currentMatch = 0;

    // 先攻/後攻統計の集計用
    let totalFirstWins = 0;
    let totalSecondWins = 0;
    let totalP1First = 0;
    let totalP2First = 0;
    let totalP1WinsWhenFirst = 0;
    let totalP1WinsWhenSecond = 0;
    let totalP2WinsWhenFirst = 0;
    let totalP2WinsWhenSecond = 0;

    // デッキ別統計の集計用
    const deckStats = {};
    selectedDeckIds.forEach(id => {
      deckStats[id] = {
        totalTurns: 0,
        totalGames: 0,
        winnerLifeTotal: 0,
        winnerLifeGames: 0,
        firstWins: 0,        // このデッキが先攻で勝った回数
        firstGames: 0,       // このデッキが先攻だった回数
        secondWins: 0,       // このデッキが後攻で勝った回数
        secondGames: 0,      // このデッキが後攻だった回数
        turnDistribution: {},
        winnerLifeList: [],
        // カード統計: cardId -> { name, attribute, cost, plays, wins, games }
        cardStats: {},
      };
    });

    // 選択されたデッキの情報を取得（カードリスト付き）
    const selectedDecks = selectedDeckIds.map(id => {
      const deck = allDecks.find(d => d.id === id);
      return {
        ...deck,
        cardList: getDeckCardIds(id),
      };
    });

    // 各対戦を実行
    // ミラーマッチモード: 各デッキ vs 自分自身
    // 通常モード: 全組み合わせ (i, j) where i < j
    const matchPairs = mirrorMatchOnly
      ? selectedDeckIds.map(id => [id, id])
      : selectedDeckIds.flatMap((id1, i) =>
          selectedDeckIds.slice(i + 1).map(id2 => [id1, id2])
        );

    for (const [deck1Id, deck2Id] of matchPairs) {
        try {
          const deck1 = buildDeck(deck1Id);
          const deck2 = buildDeck(deck2Id);

          const matchResult = simulateMultiple({
            deck1,
            deck2,
            count: gamesPerMatch,
            p1Difficulty: difficulty,
            p2Difficulty: difficulty,
            maxTurns: 15,
            summoningSickness,
            secondPlayerBonusSP: secondPlayerBonusSP ?? true,
            piercingDamage: piercingDamage ?? true,
            loneWarriorRule: loneWarriorRule ?? true,
            initialLifePoints: initialLifePoints ?? 6000,
          });

          // 先攻/後攻統計を集計（全体）
          if (matchResult.firstPlayerStats) {
            const fps = matchResult.firstPlayerStats;
            totalFirstWins += fps.firstWins || 0;
            totalSecondWins += fps.secondWins || 0;
            totalP1First += fps.p1First || 0;
            totalP2First += fps.p2First || 0;
            totalP1WinsWhenFirst += fps.p1WinsWhenFirst || 0;
            totalP1WinsWhenSecond += fps.p1WinsWhenSecond || 0;
            totalP2WinsWhenFirst += fps.p2WinsWhenFirst || 0;
            totalP2WinsWhenSecond += fps.p2WinsWhenSecond || 0;
          }

          // デッキ別統計を集計（個々の試合結果から）
          if (matchResult.games) {
            matchResult.games.forEach(game => {
              const turns = game.turns;

              // deck1 (P1側) の統計
              deckStats[deck1Id].totalTurns += turns;
              deckStats[deck1Id].totalGames += 1;
              deckStats[deck1Id].turnDistribution[turns] = (deckStats[deck1Id].turnDistribution[turns] || 0) + 1;

              // deck2 (P2側) の統計
              deckStats[deck2Id].totalTurns += turns;
              deckStats[deck2Id].totalGames += 1;
              deckStats[deck2Id].turnDistribution[turns] = (deckStats[deck2Id].turnDistribution[turns] || 0) + 1;

              // 先攻/後攻の集計
              // P1(deck1)が先攻 or P2(deck2)が先攻
              if (game.firstPlayer === 1) {
                deckStats[deck1Id].firstGames += 1;
                deckStats[deck2Id].secondGames += 1;
              } else {
                deckStats[deck1Id].secondGames += 1;
                deckStats[deck2Id].firstGames += 1;
              }

              // 勝者統計
              if (game.winner === 1) {
                // deck1が勝利
                const winnerLife = game.p1Life;
                deckStats[deck1Id].winnerLifeTotal += winnerLife;
                deckStats[deck1Id].winnerLifeGames += 1;
                deckStats[deck1Id].winnerLifeList.push(winnerLife);
                // 先攻/後攻勝利
                if (game.firstPlayer === 1) {
                  deckStats[deck1Id].firstWins += 1;
                } else {
                  deckStats[deck1Id].secondWins += 1;
                }
              } else if (game.winner === 2) {
                // deck2が勝利
                const winnerLife = game.p2Life;
                deckStats[deck2Id].winnerLifeTotal += winnerLife;
                deckStats[deck2Id].winnerLifeGames += 1;
                deckStats[deck2Id].winnerLifeList.push(winnerLife);
                // 先攻/後攻勝利
                if (game.firstPlayer === 2) {
                  deckStats[deck2Id].firstWins += 1;
                } else {
                  deckStats[deck2Id].secondWins += 1;
                }
              }
            });
          }

          // 結果を保存（双方向、詳細統計も含む）
          if (!resultMatrix[deck1Id]) resultMatrix[deck1Id] = {};
          if (!resultMatrix[deck2Id]) resultMatrix[deck2Id] = {};

          resultMatrix[deck1Id][deck2Id] = {
            wins: matchResult.p1Wins,
            losses: matchResult.p2Wins,
            draws: matchResult.draws,
            winRate: matchResult.p1WinRate,
            avgTurns: matchResult.avgTurns,
            turnDistribution: matchResult.turnDistribution,
            lifeStats: matchResult.lifeStats,
          };
          resultMatrix[deck2Id][deck1Id] = {
            wins: matchResult.p2Wins,
            losses: matchResult.p1Wins,
            draws: matchResult.draws,
            winRate: matchResult.p2WinRate,
            avgTurns: matchResult.avgTurns,
            turnDistribution: matchResult.turnDistribution,
            lifeStats: matchResult.lifeStats,
          };

          // カード統計をマージ
          if (matchResult.cardStats) {
            // deck1 (P1側) のカード統計
            matchResult.cardStats.p1.forEach(card => {
              if (!deckStats[deck1Id].cardStats[card.id]) {
                deckStats[deck1Id].cardStats[card.id] = {
                  id: card.id,
                  name: card.name,
                  attribute: card.attribute,
                  cost: card.cost,
                  type: card.type,
                  plays: 0,
                  wins: 0,
                  games: 0,
                  setsunaOpportunities: 0,
                  setsunaActivations: 0,
                };
              }
              deckStats[deck1Id].cardStats[card.id].plays += card.plays;
              deckStats[deck1Id].cardStats[card.id].wins += card.wins;
              deckStats[deck1Id].cardStats[card.id].games += card.games;
            });
            // deck2 (P2側) のカード統計
            matchResult.cardStats.p2.forEach(card => {
              if (!deckStats[deck2Id].cardStats[card.id]) {
                deckStats[deck2Id].cardStats[card.id] = {
                  id: card.id,
                  name: card.name,
                  attribute: card.attribute,
                  cost: card.cost,
                  type: card.type,
                  plays: 0,
                  wins: 0,
                  games: 0,
                  setsunaOpportunities: 0,
                  setsunaActivations: 0,
                };
              }
              deckStats[deck2Id].cardStats[card.id].plays += card.plays;
              deckStats[deck2Id].cardStats[card.id].wins += card.wins;
              deckStats[deck2Id].cardStats[card.id].games += card.games;
            });
          }

          // 刹那詠唱統計をマージ
          if (matchResult.setsunaStats?.cardStats) {
            Object.entries(matchResult.setsunaStats.cardStats).forEach(([cardId, stats]) => {
              // deck1 (P1側) の刹那詠唱統計
              if (stats.p1Opportunities > 0 || stats.p1Activations > 0) {
                if (!deckStats[deck1Id].cardStats[cardId]) {
                  deckStats[deck1Id].cardStats[cardId] = {
                    id: cardId,
                    name: stats.name,
                    attribute: stats.attribute,
                    cost: stats.cost,
                    type: 'magic',
                    plays: 0,
                    wins: 0,
                    games: 0,
                    setsunaOpportunities: 0,
                    setsunaActivations: 0,
                  };
                }
                deckStats[deck1Id].cardStats[cardId].setsunaOpportunities += stats.p1Opportunities || 0;
                deckStats[deck1Id].cardStats[cardId].setsunaActivations += stats.p1Activations || 0;
              }
              // deck2 (P2側) の刹那詠唱統計
              if (stats.p2Opportunities > 0 || stats.p2Activations > 0) {
                if (!deckStats[deck2Id].cardStats[cardId]) {
                  deckStats[deck2Id].cardStats[cardId] = {
                    id: cardId,
                    name: stats.name,
                    attribute: stats.attribute,
                    cost: stats.cost,
                    type: 'magic',
                    plays: 0,
                    wins: 0,
                    games: 0,
                    setsunaOpportunities: 0,
                    setsunaActivations: 0,
                  };
                }
                deckStats[deck2Id].cardStats[cardId].setsunaOpportunities += stats.p2Opportunities || 0;
                deckStats[deck2Id].cardStats[cardId].setsunaActivations += stats.p2Activations || 0;
              }
            });
          }
        } catch (err) {
          console.error(`対戦エラー: ${deck1Id} vs ${deck2Id}`, err);
        }

        currentMatch++;
        setProgress({ current: currentMatch, total: matchCount });

        // UIを更新するための小休止
        await new Promise(resolve => setTimeout(resolve, 0));
    }

    // 先攻/後攻統計を計算（全体）
    const totalDecidedGames = totalFirstWins + totalSecondWins;
    const firstPlayerStats = {
      firstWins: totalFirstWins,
      secondWins: totalSecondWins,
      firstWinRate: totalDecidedGames > 0 ? totalFirstWins / totalDecidedGames : 0,
      p1WinRateWhenFirst: totalP1First > 0 ? totalP1WinsWhenFirst / totalP1First : 0,
      p1WinRateWhenSecond: totalP2First > 0 ? totalP1WinsWhenSecond / totalP2First : 0,
      p2WinRateWhenFirst: totalP2First > 0 ? totalP2WinsWhenFirst / totalP2First : 0,
      p2WinRateWhenSecond: totalP1First > 0 ? totalP2WinsWhenSecond / totalP1First : 0,
    };

    // デッキ別統計を計算
    const deckStatsProcessed = {};
    Object.entries(deckStats).forEach(([deckId, stats]) => {
      // カード統計を配列に変換し、プレイ回数順にソート
      const cardStatsArray = Object.values(stats.cardStats)
        .map(card => ({
          ...card,
          playRate: stats.totalGames > 0 ? card.games / stats.totalGames : 0,
          winRate: card.games > 0 ? card.wins / card.games : 0,
        }))
        .sort((a, b) => b.plays - a.plays);

      deckStatsProcessed[deckId] = {
        avgTurns: stats.totalGames > 0 ? stats.totalTurns / stats.totalGames : 0,
        avgWinnerLife: stats.winnerLifeGames > 0 ? stats.winnerLifeTotal / stats.winnerLifeGames : 0,
        firstWinRate: stats.firstGames > 0 ? stats.firstWins / stats.firstGames : 0,
        secondWinRate: stats.secondGames > 0 ? stats.secondWins / stats.secondGames : 0,
        totalGames: stats.totalGames,
        turnDistribution: stats.turnDistribution,
        winnerLifeList: stats.winnerLifeList,
        cardStats: cardStatsArray,
      };
    });

    // 総合成績を計算
    const standings = selectedDecks.map(deck => {
      let totalWins = 0;
      let totalLosses = 0;
      let totalDraws = 0;

      // ミラーマッチモード: 自分 vs 自分の結果を使用
      // 通常モード: 他のデッキとの対戦結果を集計
      if (mirrorMatchOnly) {
        // ミラーマッチの場合、自分自身との対戦結果を使用
        if (resultMatrix[deck.id]?.[deck.id]) {
          const r = resultMatrix[deck.id][deck.id];
          totalWins = r.wins;
          totalLosses = r.losses;
          totalDraws = r.draws;
        }
      } else {
        selectedDeckIds.forEach(opponentId => {
          if (opponentId !== deck.id && resultMatrix[deck.id]?.[opponentId]) {
            const r = resultMatrix[deck.id][opponentId];
            totalWins += r.wins;
            totalLosses += r.losses;
            totalDraws += r.draws;
          }
        });
      }

      const totalGames = totalWins + totalLosses + totalDraws;
      const stats = deckStatsProcessed[deck.id] || {};
      return {
        ...deck,
        wins: totalWins,
        losses: totalLosses,
        draws: totalDraws,
        winRate: totalGames > 0 ? totalWins / totalGames : 0,
        avgTurns: stats.avgTurns || 0,
        avgWinnerLife: stats.avgWinnerLife || 0,
        firstWinRate: stats.firstWinRate || 0,
        secondWinRate: stats.secondWinRate || 0,
      };
    }).sort((a, b) => b.winRate - a.winRate);

    setResults({
      matrix: resultMatrix,
      standings,
      decks: selectedDecks,
      gamesPerMatch,
      firstPlayerStats,
      deckStats: deckStatsProcessed,
    });
    setIsRunning(false);
  };

  // 結果をAI用にコピー
  const copyResultsForAI = () => {
    if (!results) return;

    let markdown = `## 総当たり戦結果\n`;
    markdown += `- 対象デッキ数: ${results.decks.length}\n`;
    markdown += `- 対戦ごとの試行回数: ${results.gamesPerMatch}回\n\n`;

    // 総合順位（詳細統計付き）
    markdown += `## 総合順位\n`;
    markdown += `| 順位 | デッキ名 | 勝率 | 成績 | 平均T | 勝利時Life | 先攻勝率 | 後攻勝率 |\n`;
    markdown += `|------|----------|------|------|-------|------------|----------|----------|\n`;
    results.standings.forEach((deck, idx) => {
      markdown += `| ${idx + 1} | ${deck.name} | ${(deck.winRate * 100).toFixed(1)}% | ${deck.wins}勝${deck.losses}敗${deck.draws}分 | ${deck.avgTurns?.toFixed(1) || '-'} | ${deck.avgWinnerLife?.toFixed(0) || '-'} | ${deck.firstWinRate ? (deck.firstWinRate * 100).toFixed(1) + '%' : '-'} | ${deck.secondWinRate ? (deck.secondWinRate * 100).toFixed(1) + '%' : '-'} |\n`;
    });

    // 先攻/後攻統計（全体）
    if (results.firstPlayerStats) {
      const fps = results.firstPlayerStats;
      markdown += `\n## 先攻/後攻統計（全体）\n`;
      markdown += `- 先攻勝率: ${(fps.firstWinRate * 100).toFixed(1)}% (${fps.firstWins}勝${fps.secondWins}敗)\n`;
    }

    // 相性マトリクス（勝率）
    markdown += `\n## 相性マトリクス（勝率%）\n`;
    markdown += `| vs | ${results.decks.map(d => d.name.substring(0, 6)).join(' | ')} |\n`;
    markdown += `|${results.decks.map(() => '---').join('|')}|---|\n`;
    results.decks.forEach(deck => {
      const row = results.decks.map(opp => {
        if (deck.id === opp.id) return '-';
        const r = results.matrix[deck.id]?.[opp.id];
        return r ? `${(r.winRate * 100).toFixed(0)}` : '-';
      });
      markdown += `| ${deck.name.substring(0, 6)} | ${row.join(' | ')} |\n`;
    });

    // マッチアップ別平均ターン数
    markdown += `\n## マッチアップ別平均ターン数\n`;
    markdown += `| vs | ${results.decks.map(d => d.name.substring(0, 6)).join(' | ')} |\n`;
    markdown += `|${results.decks.map(() => '---').join('|')}|---|\n`;
    results.decks.forEach(deck => {
      const row = results.decks.map(opp => {
        if (deck.id === opp.id) return '-';
        const r = results.matrix[deck.id]?.[opp.id];
        return r?.avgTurns ? `${r.avgTurns.toFixed(1)}` : '-';
      });
      markdown += `| ${deck.name.substring(0, 6)} | ${row.join(' | ')} |\n`;
    });

    // デッキカード一覧
    markdown += `\n## デッキ構成（カードID一覧）\n`;
    results.decks.forEach(deck => {
      markdown += `\n### ${deck.name}\n`;
      if (deck.cardList && deck.cardList.length > 0) {
        const cardGroups = {};
        deck.cardList.forEach(({ cardId, count }) => {
          const card = allCards.find(c => c.id === cardId);
          const type = card?.type || 'unknown';
          if (!cardGroups[type]) cardGroups[type] = [];
          cardGroups[type].push({ cardId, count, name: card?.name || cardId });
        });

        ['monster', 'magic', 'field', 'phase'].forEach(type => {
          if (cardGroups[type] && cardGroups[type].length > 0) {
            markdown += `**${type === 'monster' ? 'モンスター' : type === 'magic' ? '魔法' : type === 'field' ? 'フィールド' : 'フェイズ'}:**\n`;
            cardGroups[type].forEach(({ cardId, count, name }) => {
              markdown += `- ${cardId} ${name} x${count}\n`;
            });
          }
        });
      } else {
        markdown += `（カードリスト取得不可）\n`;
      }
    });

    navigator.clipboard.writeText(markdown).then(() => {
      alert('結果をクリップボードにコピーしました');
    });
  };

  return (
    <div>
      {/* デッキ選択 */}
      <div style={styles.panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={styles.panelTitle}>
            🎯 デッキ選択 ({selectedDeckIds.length}/{allDecks.length}選択中)
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={selectAll} style={{ ...styles.smallButton, background: '#4a90d9' }}>全選択</button>
            <button onClick={deselectAll} style={{ ...styles.smallButton, background: '#666' }}>全解除</button>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '8px',
          maxHeight: '300px',
          overflowY: 'auto',
          padding: '8px',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '8px',
        }}>
          {allDecks.map(deck => (
            <label
              key={deck.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px',
                background: selectedDeckIds.includes(deck.id)
                  ? 'rgba(74, 144, 217, 0.3)'
                  : 'rgba(255,255,255,0.05)',
                borderRadius: '6px',
                cursor: 'pointer',
                border: selectedDeckIds.includes(deck.id)
                  ? '1px solid #4a90d9'
                  : '1px solid transparent',
              }}
            >
              <input
                type="checkbox"
                checked={selectedDeckIds.includes(deck.id)}
                onChange={() => toggleDeck(deck.id)}
                style={{ width: '18px', height: '18px' }}
              />
              <div>
                <div style={{ fontSize: '13px', color: '#e0e0e0' }}>{deck.name}</div>
                <div style={{ fontSize: '10px', color: '#888' }}>{deck.category}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* 設定 */}
      <div style={styles.panel}>
        <div style={styles.panelTitle}>⚙️ 設定</div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#a0a0a0', fontSize: '13px' }}>対戦ごとの回数:</span>
            <input
              type="number"
              value={gamesPerMatch}
              onChange={(e) => setGamesPerMatch(Math.max(5, Math.min(10000, parseInt(e.target.value) || 20)))}
              style={{ ...styles.input, width: '80px' }}
              min={5}
              max={10000}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#a0a0a0', fontSize: '13px' }}>AI難易度:</span>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              style={styles.select}
            >
              <option value="easy">かんたん</option>
              <option value="normal">ふつう</option>
              <option value="hard">むずかしい</option>
              <option value="evolved">🧬 進化型AI</option>
              <option value="evolvedSim">🔬 進化型AI(実験)</option>
            </select>
          </div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '4px 8px',
            background: mirrorMatchOnly ? 'rgba(74, 222, 128, 0.2)' : 'transparent',
            borderRadius: '6px',
            border: mirrorMatchOnly ? '1px solid #4ade80' : '1px solid transparent',
          }}>
            <input
              type="checkbox"
              checked={mirrorMatchOnly}
              onChange={(e) => setMirrorMatchOnly(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ color: mirrorMatchOnly ? '#4ade80' : '#a0a0a0', fontSize: '13px' }}>
              🪞 ミラーマッチのみ
            </span>
          </label>
        </div>

        <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
          <div style={{ fontSize: '13px', color: '#a0a0a0' }}>
            {mirrorMatchOnly ? 'デッキ数' : '組み合わせ'}: <span style={{ color: '#ffd700', fontWeight: 'bold' }}>{matchCount}{mirrorMatchOnly ? 'デッキ' : '通り'}</span>
            {' × '}
            {gamesPerMatch}回 = <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{totalGames}試合</span>
          </div>
        </div>
      </div>

      {/* 実行ボタン */}
      <div style={{ textAlign: 'center', margin: '16px 0' }}>
        <button
          onClick={runRoundRobin}
          disabled={isRunning || selectedDeckIds.length < (mirrorMatchOnly ? 1 : 2)}
          style={{
            ...styles.runButton,
            ...(isRunning || selectedDeckIds.length < (mirrorMatchOnly ? 1 : 2) ? styles.runButtonDisabled : {}),
          }}
        >
          {isRunning
            ? `⏳ 実行中... (${progress.current}/${progress.total})`
            : mirrorMatchOnly
              ? `▶ ミラーマッチ開始 (${matchCount}デッキ)`
              : `▶ 総当たり開始 (${matchCount}通り)`}
        </button>
      </div>

      {/* 結果表示 */}
      {results && (
        <div style={styles.panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={styles.panelTitle}>{mirrorMatchOnly ? '🪞 ミラーマッチ結果' : '📊 総当たり結果'}</div>
            <button
              onClick={copyResultsForAI}
              style={{
                padding: '6px 12px',
                background: 'linear-gradient(135deg, #2d5a4a, #1a3a2a)',
                border: '1px solid #4ade80',
                borderRadius: '6px',
                color: '#4ade80',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              📋 AI分析用にコピー
            </button>
          </div>

          {/* タブ切り替え */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button
              onClick={() => setResultTab('standings')}
              style={{
                padding: '8px 16px',
                background: resultTab === 'standings' ? '#4ade80' : 'transparent',
                border: '1px solid #4ade80',
                borderRadius: '6px',
                color: resultTab === 'standings' ? '#1a1a2e' : '#4ade80',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: resultTab === 'standings' ? 'bold' : 'normal',
              }}
            >
              🏆 順位表/相性
            </button>
            <button
              onClick={() => {
                setResultTab('cardStats');
                // 最初のデッキを自動選択
                if (!selectedStatsDeckId && results.standings.length > 0) {
                  setSelectedStatsDeckId(results.standings[0].id);
                }
              }}
              style={{
                padding: '8px 16px',
                background: resultTab === 'cardStats' ? '#60a5fa' : 'transparent',
                border: '1px solid #60a5fa',
                borderRadius: '6px',
                color: resultTab === 'cardStats' ? '#1a1a2e' : '#60a5fa',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: resultTab === 'cardStats' ? 'bold' : 'normal',
              }}
            >
              🃏 カード統計
            </button>
          </div>

          {/* 順位表/相性タブ */}
          {resultTab === 'standings' && (
            <>
          {/* 順位表（詳細統計付き） */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>🏆 総合順位:</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: isMobile ? '600px' : 'auto' }}>
                <thead>
                  <tr style={{ background: '#1a1a2e' }}>
                    <th style={{ padding: '8px', color: '#888', textAlign: 'center', width: '40px' }}>#</th>
                    <th style={{ padding: '8px', color: '#888', textAlign: 'left' }}>デッキ</th>
                    <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>勝率</th>
                    <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>成績</th>
                    <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>平均T</th>
                    <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>勝利時LP</th>
                    <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>先攻</th>
                    <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>後攻</th>
                  </tr>
                </thead>
                <tbody>
                  {results.standings.map((deck, idx) => (
                    <tr
                      key={deck.id}
                      style={{
                        background: idx === 0 ? 'rgba(255, 215, 0, 0.1)' : idx % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                      }}
                    >
                      <td style={{
                        padding: '8px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : idx === 2 ? '#cd7f32' : '#888',
                      }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: '8px', color: '#e0e0e0' }}>{deck.name}</td>
                      <td style={{ padding: '8px', textAlign: 'center', color: '#4ade80', fontWeight: 'bold' }}>
                        {(deck.winRate * 100).toFixed(1)}%
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', color: '#888', fontSize: '11px' }}>
                        {deck.wins}勝{deck.losses}敗{deck.draws}分
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', color: '#60a5fa' }}>
                        {deck.avgTurns?.toFixed(1) || '-'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', color: '#fbbf24' }}>
                        {deck.avgWinnerLife ? Math.round(deck.avgWinnerLife).toLocaleString() : '-'}
                      </td>
                      <td style={{
                        padding: '8px',
                        textAlign: 'center',
                        color: deck.firstWinRate >= 0.5 ? '#4ade80' : deck.firstWinRate > 0 ? '#f87171' : '#666',
                      }}>
                        {deck.firstWinRate ? (deck.firstWinRate * 100).toFixed(0) + '%' : '-'}
                      </td>
                      <td style={{
                        padding: '8px',
                        textAlign: 'center',
                        color: deck.secondWinRate >= 0.5 ? '#4ade80' : deck.secondWinRate > 0 ? '#f87171' : '#666',
                      }}>
                        {deck.secondWinRate ? (deck.secondWinRate * 100).toFixed(0) + '%' : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
              平均T=平均ターン数 / 勝利時LP=勝利時の平均ライフ / 先攻/後攻=各状況での勝率
            </div>
          </div>

          {/* 先攻/後攻統計（全体） */}
          {results.firstPlayerStats && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>🎯 先攻/後攻統計（全体）:</div>
              <div style={{
                display: 'flex',
                gap: '24px',
                padding: '12px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '13px', color: '#888' }}>先攻勝率:</span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#ffd700' }}>
                    {(results.firstPlayerStats.firstWinRate * 100).toFixed(1)}%
                  </span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    ({results.firstPlayerStats.firstWins}勝 / {results.firstPlayerStats.secondWins}敗)
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: '#888' }}>
                  ※各デッキの先攻/後攻勝率は上の順位表を参照
                </div>
              </div>
            </div>
          )}

          {/* 相性マトリクス（ミラーマッチモードでは非表示） */}
          {!mirrorMatchOnly && (
          <div>
            <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>📋 相性マトリクス（勝率%）:</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '11px',
              }}>
                <thead>
                  <tr>
                    <th style={{ padding: '6px', background: '#1a1a2e', color: '#888', textAlign: 'left' }}>vs</th>
                    {results.decks.map(deck => (
                      <th key={deck.id} style={{
                        padding: '6px',
                        background: '#1a1a2e',
                        color: '#888',
                        textAlign: 'center',
                        maxWidth: '60px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {deck.name.substring(0, 5)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.decks.map(rowDeck => (
                    <tr key={rowDeck.id}>
                      <td style={{
                        padding: '6px',
                        background: '#1a1a2e',
                        color: '#c0c0c0',
                        fontWeight: 'bold',
                      }}>
                        {rowDeck.name.substring(0, 8)}
                      </td>
                      {results.decks.map(colDeck => {
                        if (rowDeck.id === colDeck.id) {
                          return (
                            <td key={colDeck.id} style={{
                              padding: '6px',
                              textAlign: 'center',
                              background: '#2a2a3e',
                              color: '#666',
                            }}>
                              -
                            </td>
                          );
                        }
                        const r = results.matrix[rowDeck.id]?.[colDeck.id];
                        const winRate = r ? r.winRate * 100 : 0;
                        const bgColor = winRate >= 60 ? 'rgba(74, 222, 128, 0.3)'
                          : winRate >= 50 ? 'rgba(74, 222, 128, 0.15)'
                          : winRate >= 40 ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(217, 74, 74, 0.2)';
                        const textColor = winRate >= 55 ? '#4ade80' : winRate <= 45 ? '#f87171' : '#c0c0c0';
                        return (
                          <td key={colDeck.id} style={{
                            padding: '6px',
                            textAlign: 'center',
                            background: bgColor,
                            color: textColor,
                            fontWeight: winRate >= 60 || winRate <= 40 ? 'bold' : 'normal',
                          }}>
                            {winRate.toFixed(0)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* マッチアップ別平均ターン数（ミラーマッチモードでは非表示） */}
          {!mirrorMatchOnly && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontSize: '14px', color: '#a0a0a0', marginBottom: '8px' }}>⏱️ マッチアップ別平均ターン数:</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '11px',
              }}>
                <thead>
                  <tr>
                    <th style={{ padding: '6px', background: '#1a1a2e', color: '#888', textAlign: 'left' }}>vs</th>
                    {results.decks.map(deck => (
                      <th key={deck.id} style={{
                        padding: '6px',
                        background: '#1a1a2e',
                        color: '#888',
                        textAlign: 'center',
                        maxWidth: '60px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {deck.name.substring(0, 5)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.decks.map(rowDeck => (
                    <tr key={rowDeck.id}>
                      <td style={{
                        padding: '6px',
                        background: '#1a1a2e',
                        color: '#c0c0c0',
                        fontWeight: 'bold',
                      }}>
                        {rowDeck.name.substring(0, 8)}
                      </td>
                      {results.decks.map(colDeck => {
                        if (rowDeck.id === colDeck.id) {
                          return (
                            <td key={colDeck.id} style={{
                              padding: '6px',
                              textAlign: 'center',
                              background: '#2a2a3e',
                              color: '#666',
                            }}>
                              -
                            </td>
                          );
                        }
                        const r = results.matrix[rowDeck.id]?.[colDeck.id];
                        const avgTurns = r?.avgTurns || 0;
                        // 色分け: 早い（青）、標準（白）、遅い（黄）
                        const textColor = avgTurns <= 5 ? '#60a5fa' : avgTurns <= 8 ? '#e0e0e0' : '#fbbf24';
                        return (
                          <td key={colDeck.id} style={{
                            padding: '6px',
                            textAlign: 'center',
                            background: 'rgba(255,255,255,0.02)',
                            color: textColor,
                          }}>
                            {avgTurns.toFixed(1)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
              青=速攻（〜5T） / 白=標準（5〜8T） / 黄=持久（8T〜）
            </div>
          </div>
          )}
            </>
          )}

          {/* カード統計タブ */}
          {resultTab === 'cardStats' && (
            <div>
              {/* デッキ選択 */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '14px', color: '#a0a0a0' }}>デッキ選択:</span>
                  <select
                    value={selectedStatsDeckId || ''}
                    onChange={(e) => setSelectedStatsDeckId(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      background: '#2a2a3e',
                      border: '1px solid #4a4a5e',
                      borderRadius: '6px',
                      color: '#e0e0e0',
                      fontSize: '13px',
                      minWidth: '200px',
                    }}
                  >
                    {results.standings.map(deck => (
                      <option key={deck.id} value={deck.id}>
                        {deck.name} ({(deck.winRate * 100).toFixed(1)}%)
                      </option>
                    ))}
                  </select>
                  {selectedStatsDeckId && results.deckStats[selectedStatsDeckId] && (
                    <button
                      onClick={() => {
                        const stats = results.deckStats[selectedStatsDeckId];
                        const deckName = results.standings.find(d => d.id === selectedStatsDeckId)?.name || selectedStatsDeckId;
                        const typeLabels = { monster: 'モンスター', magic: '魔法', field: 'フィールド', phase: 'フェイズ' };
                        // 刹那詠唱カードがあるかチェック
                        const hasSetsunaCards = stats.cardStats.some(c => (c.setsunaOpportunities || 0) > 0 || (c.setsunaActivations || 0) > 0);
                        let markdown = `## ${deckName} カード統計\n\n`;
                        if (hasSetsunaCards) {
                          markdown += `| カード名 | タイプ | 属性 | コスト | プレイ回数 | 登場率 | プレイ時勝率 | 刹那機会 | 刹那発動 |\n`;
                          markdown += `|---------|-------|------|-------|----------|-------|------------|---------|----------|\n`;
                        } else {
                          markdown += `| カード名 | タイプ | 属性 | コスト | プレイ回数 | 登場率 | プレイ時勝率 |\n`;
                          markdown += `|---------|-------|------|-------|----------|-------|------------|\n`;
                        }
                        stats.cardStats.forEach(card => {
                          const typeName = typeLabels[card.type] || card.type || '-';
                          const hasSetsunaData = (card.setsunaOpportunities || 0) > 0 || (card.setsunaActivations || 0) > 0;
                          const setsunaRate = card.setsunaOpportunities > 0 ? ((card.setsunaActivations || 0) / card.setsunaOpportunities * 100).toFixed(0) : '-';
                          if (hasSetsunaCards) {
                            const setsunaOpp = hasSetsunaData ? `${card.setsunaOpportunities}回` : '-';
                            const setsunaAct = hasSetsunaData ? `${card.setsunaActivations || 0}回 (${setsunaRate}%)` : '-';
                            markdown += `| ${card.name}${hasSetsunaData ? ' ⚡刹那' : ''} | ${typeName} | ${card.attribute} | ${card.cost} | ${card.plays}回 | ${(card.playRate * 100).toFixed(0)}% | ${(card.winRate * 100).toFixed(1)}% | ${setsunaOpp} | ${setsunaAct} |\n`;
                          } else {
                            markdown += `| ${card.name} | ${typeName} | ${card.attribute} | ${card.cost} | ${card.plays}回 | ${(card.playRate * 100).toFixed(0)}% | ${(card.winRate * 100).toFixed(1)}% |\n`;
                          }
                        });
                        navigator.clipboard.writeText(markdown);
                        alert('カード統計をコピーしました');
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #2d4a5a, #1a2a3a)',
                        border: '1px solid #60a5fa',
                        borderRadius: '6px',
                        color: '#60a5fa',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      📋 このデッキのカード統計をコピー
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const typeLabels = { monster: 'モンスター', magic: '魔法', field: 'フィールド', phase: 'フェイズ' };
                      let markdown = `# 全デッキ カード統計\n\n`;

                      results.standings.forEach(deck => {
                        const stats = results.deckStats[deck.id];
                        if (!stats || !stats.cardStats || stats.cardStats.length === 0) return;

                        // 刹那詠唱カードがあるかチェック
                        const hasSetsunaCards = stats.cardStats.some(c => (c.setsunaOpportunities || 0) > 0 || (c.setsunaActivations || 0) > 0);

                        markdown += `## ${deck.name} (勝率: ${(deck.winRate * 100).toFixed(1)}%)\n\n`;
                        if (hasSetsunaCards) {
                          markdown += `| カード名 | タイプ | 属性 | コスト | プレイ回数 | 登場率 | プレイ時勝率 | 刹那機会 | 刹那発動 |\n`;
                          markdown += `|---------|-------|------|-------|----------|-------|------------|---------|----------|\n`;
                        } else {
                          markdown += `| カード名 | タイプ | 属性 | コスト | プレイ回数 | 登場率 | プレイ時勝率 |\n`;
                          markdown += `|---------|-------|------|-------|----------|-------|------------|\n`;
                        }
                        stats.cardStats.forEach(card => {
                          const typeName = typeLabels[card.type] || card.type || '-';
                          const hasSetsunaData = (card.setsunaOpportunities || 0) > 0 || (card.setsunaActivations || 0) > 0;
                          const setsunaRate = card.setsunaOpportunities > 0 ? ((card.setsunaActivations || 0) / card.setsunaOpportunities * 100).toFixed(0) : '-';
                          if (hasSetsunaCards) {
                            const setsunaOpp = hasSetsunaData ? `${card.setsunaOpportunities}回` : '-';
                            const setsunaAct = hasSetsunaData ? `${card.setsunaActivations || 0}回 (${setsunaRate}%)` : '-';
                            markdown += `| ${card.name}${hasSetsunaData ? ' ⚡刹那' : ''} | ${typeName} | ${card.attribute} | ${card.cost} | ${card.plays}回 | ${(card.playRate * 100).toFixed(0)}% | ${(card.winRate * 100).toFixed(1)}% | ${setsunaOpp} | ${setsunaAct} |\n`;
                          } else {
                            markdown += `| ${card.name} | ${typeName} | ${card.attribute} | ${card.cost} | ${card.plays}回 | ${(card.playRate * 100).toFixed(0)}% | ${(card.winRate * 100).toFixed(1)}% |\n`;
                          }
                        });
                        markdown += `\n`;
                      });

                      navigator.clipboard.writeText(markdown);
                      alert('全デッキのカード統計をコピーしました');
                    }}
                    style={{
                      padding: '6px 12px',
                      background: 'linear-gradient(135deg, #4a2d5a, #2a1a3a)',
                      border: '1px solid #a855f7',
                      borderRadius: '6px',
                      color: '#a855f7',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    📋 全デッキのカード統計をコピー
                  </button>
                </div>
              </div>

              {/* カード統計テーブル */}
              {selectedStatsDeckId && results.deckStats[selectedStatsDeckId] && (() => {
                const cardStats = results.deckStats[selectedStatsDeckId].cardStats;
                // 刹那詠唱カードがあるかチェック
                const hasSetsunaCards = cardStats.some(c => (c.setsunaOpportunities || 0) > 0 || (c.setsunaActivations || 0) > 0);
                return (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#1a1a2e' }}>
                        <th style={{ padding: '8px', color: '#888', textAlign: 'left' }}>カード名</th>
                        <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>タイプ</th>
                        <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>属性</th>
                        <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>コスト</th>
                        <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>プレイ回数</th>
                        <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>登場率</th>
                        <th style={{ padding: '8px', color: '#888', textAlign: 'center' }}>プレイ時勝率</th>
                        {hasSetsunaCards && (
                          <>
                            <th style={{ padding: '8px', color: '#f0abfc', textAlign: 'center' }}>刹那機会</th>
                            <th style={{ padding: '8px', color: '#f0abfc', textAlign: 'center' }}>刹那発動</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {cardStats.map((card, idx) => {
                        const winRateColor = card.winRate >= 0.55 ? '#4ade80'
                          : card.winRate >= 0.45 ? '#e0e0e0'
                          : '#f87171';
                        const typeLabel = {
                          monster: '⚔️',
                          magic: '✨',
                          field: '🏔️',
                          phase: '🌀',
                        }[card.type] || card.type || '-';
                        const hasSetsunaData = (card.setsunaOpportunities || 0) > 0 || (card.setsunaActivations || 0) > 0;
                        const setsunaRate = card.setsunaOpportunities > 0
                          ? ((card.setsunaActivations || 0) / card.setsunaOpportunities * 100).toFixed(0)
                          : null;
                        return (
                          <tr
                            key={card.id}
                            style={{
                              background: idx % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                              borderBottom: '1px solid rgba(255,255,255,0.05)',
                            }}
                          >
                            <td style={{ padding: '8px', color: '#e0e0e0' }}>
                              {card.name}
                              {hasSetsunaData && <span style={{ marginLeft: '4px', color: '#f0abfc', fontSize: '10px' }}>⚡刹那</span>}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{typeLabel}</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#a0a0a0' }}>{card.attribute}</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#fbbf24' }}>{card.cost}</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#60a5fa' }}>{card.plays}回</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#a0a0a0' }}>
                              {(card.playRate * 100).toFixed(0)}%
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', color: winRateColor, fontWeight: 'bold' }}>
                              {(card.winRate * 100).toFixed(1)}%
                            </td>
                            {hasSetsunaCards && (
                              <>
                                <td style={{ padding: '8px', textAlign: 'center', color: hasSetsunaData ? '#f0abfc' : '#555' }}>
                                  {hasSetsunaData ? `${card.setsunaOpportunities}回` : '-'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', color: hasSetsunaData ? '#c084fc' : '#555' }}>
                                  {hasSetsunaData ? `${card.setsunaActivations || 0}回${setsunaRate !== null ? ` (${setsunaRate}%)` : ''}` : '-'}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                    登場率 = そのカードがゲーム終了時に場または墓地にあった試合の割合<br/>
                    プレイ時勝率 = そのカードが登場した試合のうち勝利した割合
                    {hasSetsunaCards && (
                      <>
                        <br/>
                        刹那機会 = 刹那詠唱可能なタイミングで発動可能だった回数<br/>
                        刹那発動 = 実際に刹那詠唱で発動した回数（発動率%）
                      </>
                    )}
                  </div>
                </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ========================================
// メインコンポーネント
// ========================================

const AIAnalysisScreen = ({ allCards, playerData, onClose }) => {
  const isMobile = useIsMobile();

  // 画面モード: 'analysis' = 詳細分析, 'roundrobin' = 総当たり
  const [screenMode, setScreenMode] = useState('analysis');

  // デッキカテゴリ
  const [p1DeckCategory, setP1DeckCategory] = useState('preset');
  const [p2DeckCategory, setP2DeckCategory] = useState('preset');

  // 設定
  const [p1Deck, setP1Deck] = useState('preset:starter');
  const [p2Deck, setP2Deck] = useState('preset:water');
  const [p1Difficulty, setP1Difficulty] = useState('hard');
  const [p2Difficulty, setP2Difficulty] = useState('hard');
  const [maxTurns, setMaxTurns] = useState(15);

  // バッチモード設定
  const [batchMode, setBatchMode] = useState(false);
  const [batchCount, setBatchCount] = useState(20);

  // 実行状態
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [error, setError] = useState(null);

  // 表示フィルター
  const [activeTab, setActiveTab] = useState('all');
  const [turnFilter, setTurnFilter] = useState('all');
  const [playerFilter, setPlayerFilter] = useState('all');

  // ユーザーデッキリスト
  const userDecks = playerData?.userDecks || [];

  /**
   * デッキ選択値からデッキを構築
   * @param {string} deckValue - "category:id" 形式の値
   * @returns {Array} デッキ（カードインスタンスの配列）
   */
  const buildDeckFromSelection = (deckValue) => {
    const [category, id] = deckValue.split(':');

    switch (category) {
      case 'preset':
        return getDeckByName(id, allCards);

      case 'npc': {
        const npcDeck = NPC_DECKS[id];
        if (!npcDeck) throw new Error(`NPCデッキ「${id}」が見つかりません`);

        // カードIDリストに展開
        const cardIds = expandDeckToCardIds(npcDeck);

        // カードインスタンスを生成
        const deck = cardIds.map(cardId => {
          const cardData = allCards.find(c => c.id === cardId);
          if (!cardData) {
            console.warn(`カードが見つかりません: ${cardId}`);
            return null;
          }
          return {
            ...cardData,
            uniqueId: `${cardId}_${Math.random().toString(36).substr(2, 9)}`,
          };
        }).filter(Boolean);

        return shuffle(deck);
      }

      case 'mydeck': {
        const deckIndex = parseInt(id);
        const userDeck = userDecks[deckIndex];
        if (!userDeck) throw new Error(`マイデッキが見つかりません`);

        // ユーザーデッキからカードインスタンスを生成
        const deck = [];
        for (const deckCard of userDeck.cards || []) {
          const cardData = allCards.find(c => c.id === deckCard.cardId);
          if (cardData) {
            deck.push({
              ...cardData,
              uniqueId: `${cardData.id}_${Math.random().toString(36).substr(2, 9)}`,
            });
          }
        }

        if (deck.length < 30) {
          throw new Error(`デッキが30枚未満です（${deck.length}枚）`);
        }

        return shuffle(deck);
      }

      default:
        throw new Error(`不明なデッキカテゴリ: ${category}`);
    }
  };

  /**
   * デッキ選択値からデッキ名を取得
   * @param {string} deckValue - "category:id" 形式の値
   * @returns {string} デッキ名
   */
  const getDeckNameFromSelection = (deckValue) => {
    const [category, id] = deckValue.split(':');

    switch (category) {
      case 'preset': {
        const presetDeck = PREBUILT_DECKS[id];
        return presetDeck?.name || id;
      }
      case 'npc': {
        const npcDeck = NPC_DECKS[id];
        return npcDeck?.name || id;
      }
      case 'mydeck': {
        const deckIndex = parseInt(id);
        const userDeck = userDecks[deckIndex];
        return userDeck?.name || `マイデッキ${deckIndex + 1}`;
      }
      default:
        return id;
    }
  };

  // シミュレーション実行
  const runSimulation = () => {
    setIsRunning(true);
    setError(null);

    // 召喚酔い・後攻ボーナスSP・貫通ダメージ・孤軍奮闘・初期LP設定を取得
    const { summoningSickness, secondPlayerBonusSP, piercingDamage, loneWarriorRule, initialLifePoints } = getPlayerDeckSettings();

    try {
      if (batchMode) {
        // バッチモード：複数回シミュレーション
        const deck1 = buildDeckFromSelection(p1Deck);
        const deck2 = buildDeckFromSelection(p2Deck);

        const batchResults = simulateMultiple({
          deck1,
          deck2,
          p1Difficulty,
          p2Difficulty,
          maxTurns,
          count: batchCount,
          summoningSickness,
          secondPlayerBonusSP,
          piercingDamage,
          initialLifePoints,
        });

        setBatchResult(batchResults);
        setResult(null); // 詳細結果をクリア
      } else {
        // 詳細モード：1回シミュレーション
        const deck1 = buildDeckFromSelection(p1Deck);
        const deck2 = buildDeckFromSelection(p2Deck);

        const simResult = simulateGameDebug({
          deck1,
          deck2,
          p1Difficulty,
          p2Difficulty,
          maxTurns,
          summoningSickness,
          secondPlayerBonusSP,
          piercingDamage,
          initialLifePoints,
        });

        setResult(simResult);
        setBatchResult(null); // バッチ結果をクリア
      }

      // フィルターをリセット
      setTurnFilter('all');
      setPlayerFilter('all');
    } catch (err) {
      console.error('Simulation error:', err);
      setError(err.message);
    } finally {
      setIsRunning(false);
    }
  };

  // ターン選択肢を生成
  const turnOptions = useMemo(() => {
    if (!result) return [];
    const turns = [...new Set(result.aiDecisions.map(d => d.turn))].sort((a, b) => a - b);
    return turns;
  }, [result]);

  return (
    <div style={styles.overlay}>
      <div style={{
        ...styles.container,
        padding: isMobile ? '12px' : '20px',
      }}>
        {/* ヘッダー */}
        <div style={styles.header}>
          <h1 style={{
            ...styles.title,
            fontSize: isMobile ? '18px' : '24px',
          }}>
            🤖 AI思考分析
          </h1>
          <button style={styles.closeButton} onClick={onClose}>
            ✕ 閉じる
          </button>
        </div>

        {/* 画面モードタブ */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '16px',
          padding: '4px',
          background: 'rgba(0,0,0,0.3)',
          borderRadius: '8px',
        }}>
          <button
            onClick={() => setScreenMode('analysis')}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: screenMode === 'analysis'
                ? 'linear-gradient(135deg, #4a90d9, #357abd)'
                : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: screenMode === 'analysis' ? '#fff' : '#888',
              fontSize: '14px',
              fontWeight: screenMode === 'analysis' ? 'bold' : 'normal',
              cursor: 'pointer',
            }}
          >
            📊 対戦分析
          </button>
          <button
            onClick={() => setScreenMode('roundrobin')}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: screenMode === 'roundrobin'
                ? 'linear-gradient(135deg, #9d4ce6, #7b3bb3)'
                : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: screenMode === 'roundrobin' ? '#fff' : '#888',
              fontSize: '14px',
              fontWeight: screenMode === 'roundrobin' ? 'bold' : 'normal',
              cursor: 'pointer',
            }}
          >
            🔄 総当たり分析
          </button>
        </div>

        {/* 対戦分析モード */}
        {screenMode === 'analysis' && (
          <>
        {/* 設定パネル */}
        <SettingsPanel
          p1DeckCategory={p1DeckCategory}
          setP1DeckCategory={setP1DeckCategory}
          p1Deck={p1Deck}
          setP1Deck={setP1Deck}
          p2DeckCategory={p2DeckCategory}
          setP2DeckCategory={setP2DeckCategory}
          p2Deck={p2Deck}
          setP2Deck={setP2Deck}
          p1Difficulty={p1Difficulty}
          setP1Difficulty={setP1Difficulty}
          p2Difficulty={p2Difficulty}
          setP2Difficulty={setP2Difficulty}
          maxTurns={maxTurns}
          setMaxTurns={setMaxTurns}
          batchMode={batchMode}
          setBatchMode={setBatchMode}
          batchCount={batchCount}
          setBatchCount={setBatchCount}
          isRunning={isRunning}
          onRun={runSimulation}
          isMobile={isMobile}
          userDecks={userDecks}
        />

        {/* エラー表示 */}
        {error && (
          <div style={styles.error}>
            ❌ エラー: {error}
          </div>
        )}

        {/* バッチモード結果サマリー */}
        {batchMode && <BatchResultSummary
          result={batchResult}
          p1DeckName={getDeckNameFromSelection(p1Deck)}
          p2DeckName={getDeckNameFromSelection(p2Deck)}
        />}

        {/* 詳細モード結果サマリー */}
        {!batchMode && <ResultSummary
          result={result}
          p1DeckName={getDeckNameFromSelection(p1Deck)}
          p2DeckName={getDeckNameFromSelection(p2Deck)}
          p1Difficulty={p1Difficulty}
          p2Difficulty={p2Difficulty}
        />}

        {/* タブとフィルター（詳細モードのみ） */}
        {!batchMode && result && (
          <>
            <div style={styles.tabs}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  style={{
                    ...styles.tab,
                    ...(activeTab === tab.id ? styles.tabActive : {}),
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={styles.filters}>
              <div style={styles.row}>
                <span style={{ ...styles.label, minWidth: '80px' }}>ターン絞込:</span>
                <select
                  style={styles.select}
                  value={turnFilter}
                  onChange={(e) => setTurnFilter(e.target.value)}
                >
                  <option value="all">全て</option>
                  {turnOptions.map((t) => (
                    <option key={t} value={t}>ターン {t}</option>
                  ))}
                </select>
              </div>
              <div style={styles.row}>
                <span style={{ ...styles.label, minWidth: '80px' }}>プレイヤー:</span>
                <select
                  style={styles.select}
                  value={playerFilter}
                  onChange={(e) => setPlayerFilter(e.target.value)}
                >
                  <option value="all">両方</option>
                  <option value="1">P1のみ</option>
                  <option value="2">P2のみ</option>
                </select>
              </div>
            </div>
          </>
        )}

        {/* 詳細ログ（詳細モードのみ） */}
        {!batchMode && (
          <DetailLog
            result={result}
            activeTab={activeTab}
            turnFilter={turnFilter}
            playerFilter={playerFilter}
          />
        )}
          </>
        )}

        {/* 総当たり分析モード */}
        {screenMode === 'roundrobin' && (
          <RoundRobinTab
            allCards={allCards}
            playerData={playerData}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
};

export default AIAnalysisScreen;
